import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import moment from 'moment-timezone';
import { apiService } from '../../services/api.service';
import { trackingService } from '../../services/tracking.service';
import { environment } from '../../config/environment';
import { socketService } from '../../services/socket.service';
import { Modal } from 'react-bootstrap';
import { Loader } from '@googlemaps/js-api-loader';
import './Summary.css';

interface TripDetail {
  uniqueId: any;
  uniqueId2: any;
  pickup: any;
  drop: any;
  user: any;
  driver: any;
  driver2?: any;
  country: any;
  invoice: any;
  isPaid: number;
  isPaid1?: number;
  isPaid2?: number;
  is_provider_accepted: number;
  is_provider_accepted1?: number;
  is_provider_accepted2?: number;
  is_trip_cancelled: number;
  is_trip_cancelled1?: number;
  is_trip_cancelled2?: number;
  is_trip_completed: number;
  is_trip_completed1?: number;
  is_trip_completed2?: number;
  pickupTime: Date | null;
  returnPickupTime?: Date | null;
  timezone: string;
  vehicle: any;
  note?: string;
  origin?: string;
  total1?: number;
  total2?: number;
  stops?: any[];
  direction?: any;
}

const Summary: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const tripId = searchParams.get('tripId');
  const tripId2 = searchParams.get('tripId2');
  const coid = searchParams.get('coid') || searchParams.get('coId') || '';
  const cuid = searchParams.get('cuid') || searchParams.get('cuId') || '';

  const [isLoading, setIsLoading] = useState(false);
  const [companyName, setCompanyName] = useState('');
  const [logo, setLogo] = useState('');
  const [mollie, setMollie] = useState(false);
  const [showStripe, setShowStripe] = useState(false);
  const ranOnce = useRef(false);
  const [redirectUrl, setRedirectUrl] = useState('');
  const paymentMethod = useRef<any>(null);
  const stripePublicKey = useRef<any>(null);
  const stripe = useRef<any>(null);
  const paymentBeforeAccept = useRef<any>(false);
  const returnTrip = useRef<any>(false);
  const [cardElement, setCardElement] = useState<any>(null);
  const [idealElement, setIdealElement] = useState<any>(null);
  const [tripDetail, setTripDetail] = useState<TripDetail>({
    pickup: { address: '' },
    drop: { address: '' },
    user: { name: '', email: '', phone: '', flight: '', phoneCode: '', pax: 1, bags: 1 },
    driver: null,
    country: { currencySign: '€', currencyCode: 'EUR' },
    invoice: { total: 0, paymentMode: 0 },
    isPaid: 0,
    uniqueId: 0,
    uniqueId2: 0,
    is_provider_accepted: 0,
    is_trip_cancelled: 0,
    is_trip_completed: 0,
    pickupTime: null,
    timezone: 'Europe/Amsterdam',
    vehicle: { name: '' },
  });

  const [selectedLanguage, setSelectedLanguage] = useState({
    value: 'en',
    label: 'label-title.english',
    img: '/assets/images/flags/en.gif',
  });

  const [showTracking, setShowTracking] = useState(false);
  const summaryTracked = useRef<boolean>(false);
  const mapKeyRef = useRef<string>('');
  const prevDriverIdRef = useRef<string | null>(null);
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<any>(null);
  const driverMarkerRef = useRef<any>(null);
  const stopsMarkersRef = useRef<any[]>([]);
  const polylineRef = useRef<any>(null);

  const languages = [
    { value: 'en', label: 'label-title.english', img: '/assets/images/flags/en.gif' },
    { value: 'nl', label: 'label-title.dutch', img: '/assets/images/flags/nl.gif' },
    { value: 'fr', label: 'label-title.french', img: '/assets/images/flags/fr.gif' },
    { value: 'es', label: 'label-title.spanish', img: '/assets/images/flags/sp.gif' },
  ];

  useEffect(() => {
    if (ranOnce.current) return;
    ranOnce.current = true;
    const languageCode = /* localStorage.getItem('languageCode') || */ 'en';
    const lang = languages.find(l => l.value === languageCode) || languages[0];
    setSelectedLanguage(lang);
    apiService.selectedLanguage = lang;
    i18n.changeLanguage(languageCode);

    if (tripId2) {
      returnTrip.current = true;
    }

    getSetting();
    socketService.setupSocketConnection();

    return () => {
      if (tripId) {
        socketService.removeListener(tripId);
      }
      if (tripId2) {
        socketService.removeListener(tripId2);
      }
    };
  }, []);

  useEffect(() => {
    const currentDriverId = tripDetail.driver?._id || null;
    const prevDriverId = prevDriverIdRef.current;

    if (currentDriverId !== prevDriverId) {
      // Remove old listener
      if (prevDriverId) {
        socketService.removeListener(`${prevDriverId}_location`);
      }

      // Add new listener
      if (currentDriverId) {
        socketService.listen(`${currentDriverId}_location`, (res: any) => {
          console.log(res)
          if (res.driverLocation && res.driverLocation[0] && res.driverLocation[1]) {
            setTripDetail(prev => {
              if (prev.driver && prev.driver._id === currentDriverId) {
                return {
                  ...prev,
                  driver: {
                    ...prev.driver,
                    driverLocation: res.driverLocation,
                    locationUpdatedTime: new Date()
                  }
                };
              }
              return prev;
            });
          }
        });
      }

      prevDriverIdRef.current = currentDriverId;
    }
  }, [tripDetail.driver?._id]);

  useEffect(() => {
    return () => {
      if (prevDriverIdRef.current) {
        socketService.removeListener(`${prevDriverIdRef.current}_location`);
      }
    };
  }, []);

  useEffect(() => {
    let active = true;
    if (showTracking && mapContainerRef.current) {
      const initMap = async () => {
        try {
          const mapKey = mapKeyRef.current || environment.GOOGLE_PLACES_API_KEY;
          if (!mapKey) {
            console.error("Google Maps API Key is missing");
            return;
          }

          const loader = new Loader({
            apiKey: mapKey,
            version: 'weekly',
            libraries: ['places'],
          });
          await loader.load();

          if (!active) return;
          if (!window.google || !window.google.maps) return;

          // Clear previous map objects if any
          if (polylineRef.current) {
            polylineRef.current.setMap(null);
            polylineRef.current = null;
          }
          stopsMarkersRef.current.forEach(m => m.setMap(null));
          stopsMarkersRef.current = [];
          if (driverMarkerRef.current) {
            driverMarkerRef.current.setMap(null);
            driverMarkerRef.current = null;
          }

          // Create map
          const map = new window.google.maps.Map(mapContainerRef.current, {
            zoom: 12,
            center: { lat: 0, lng: 0 },
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: true
          });
          mapRef.current = map;

          const bounds = new window.google.maps.LatLngBounds();

          // 1. Draw pickup, drop, stops markers
          if (tripDetail.stops && Array.isArray(tripDetail.stops)) {
            tripDetail.stops.forEach((stop: any) => {
              if (stop.location && stop.location[0] && stop.location[1]) {
                const position = { lat: stop.location[1], lng: stop.location[0] };
                bounds.extend(position);

                let iconUrl = '/assets/map_pin/stop_pin.svg';
                if (stop.stopType === 'pickup') {
                  iconUrl = '/assets/map_pin/destination_pin.svg';
                } else if (stop.stopType === 'drop') {
                  iconUrl = '/assets/map_pin/pickup_pin.svg';
                }

                const marker = new window.google.maps.Marker({
                  position,
                  map,
                  title: stop.address || '',
                  icon: {
                    url: iconUrl,
                    scaledSize: new window.google.maps.Size(32, 32),
                    origin: new window.google.maps.Point(0, 0),
                    anchor: new window.google.maps.Point(16, 32)
                  }
                });
                stopsMarkersRef.current.push(marker);
              }
            });
          }

          // 2. Draw driver marker
          if (tripDetail.driver && tripDetail.driver.driverLocation && tripDetail.driver.driverLocation[0] && tripDetail.driver.driverLocation[1]) {
            const position = { lat: tripDetail.driver.driverLocation[1], lng: tripDetail.driver.driverLocation[0] };
            bounds.extend(position);

            const driverIcon = /*tripDetail.driver.picture ? {
              url: environment.FILE_URL + tripDetail.driver.picture,
              scaledSize: new window.google.maps.Size(40, 40),
              origin: new window.google.maps.Point(0, 0),
              anchor: new window.google.maps.Point(20, 20)
            } :*/ {
              url: "/assets/map_pin/driver_pin.svg",
              scaledSize: new window.google.maps.Size(36, 36),
              origin: new window.google.maps.Point(0, 0),
              anchor: new window.google.maps.Point(18, 18)
            };
            console.log(driverIcon)
            const marker = new window.google.maps.Marker({
              position,
              map,
              title: tripDetail.driver.name || 'Driver',
              icon: driverIcon,
            });
            driverMarkerRef.current = marker;
          }

          // 3. Draw direction polyline
          let parsedDirection: any[] = [];
          if (typeof tripDetail.direction === 'string') {
            try {
              parsedDirection = JSON.parse(tripDetail.direction);
            } catch (e) {
              console.error("Error parsing direction", e);
            }
          } else if (Array.isArray(tripDetail.direction)) {
            parsedDirection = tripDetail.direction;
          }

          if (parsedDirection && parsedDirection.length > 0) {
            const path = parsedDirection.map((coords: any) => {
              const pt = { lat: coords[0], lng: coords[1] };
              bounds.extend(pt);
              return pt;
            });

            const polyline = new window.google.maps.Polyline({
              path,
              geodesic: true,
              strokeColor: "#FF0000",
              strokeOpacity: 0.8,
              strokeWeight: 4,
            });
            polyline.setMap(map);
            polylineRef.current = polyline;
          }

          // Fit bounds
          if (!bounds.isEmpty()) {
            map.fitBounds(bounds);
          }
        } catch (error) {
          console.error("Error loading map:", error);
        }
      };

      initMap();
    }

    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showTracking, tripDetail.stops, tripDetail.direction]);

  useEffect(() => {
    if (showTracking && mapRef.current && driverMarkerRef.current && tripDetail.driver?.driverLocation) {
      const lat = tripDetail.driver.driverLocation[1];
      const lng = tripDetail.driver.driverLocation[0];
      if (lat && lng) {
        driverMarkerRef.current.setPosition({ lat, lng });
      }
    }
  }, [tripDetail.driver?.driverLocation, showTracking]);

  const getSetting = async () => {
    try {
      const response = await apiService.getData(
        environment['Booking Tool'].settingDetails,
        {
          address: searchParams.get('address') || '',
          coid: coid,
          cuid: cuid,
        }
      );

      if (response.data) {
        apiService.rederScript(response);
        trackingService.init(response, 'summary');
        let mollieCheck = response.data.bookingToolDetail?.paymentGateway === "Mollie" ? true : false;
        setCompanyName(response.data.companyName || '');
        setLogo(response.data.logo || '');
        stripePublicKey.current = response.data.bookingToolDetail?.stripePublicKey || '';
        setMollie(mollieCheck);
        setRedirectUrl(response.data.bookingToolDetail?.redirectUrl || '');
        paymentMethod.current = response.data.bookingToolDetail?.paymetmethod
        paymentBeforeAccept.current = response.data.bookingToolDetail?.paymentBeforeAccept
        if (stripePublicKey.current) {
          loadStripe(stripePublicKey.current, paymentMethod.current);
        }

        // Set theme colors
        if (response.data.bookingToolDetail) {
          if (response.data.bookingToolDetail.mapKey) {
            mapKeyRef.current = response.data.bookingToolDetail.mapKey;
          }
          if (response.data.bookingToolDetail.language) {
            let language: any = languages.find((lang: any) => lang.value === response.data.bookingToolDetail.language)
            if (language) {
              switchLanguage(language)
            }
          }
          const root = document.documentElement;
          if (response.data.bookingToolDetail.secondaryTextColorCode) {
            root.style.setProperty('--secondary-text-color', response.data.bookingToolDetail.secondaryTextColorCode);
          }
          if (response.data.bookingToolDetail.primaryColorCode) {
            root.style.setProperty('--theme-color-code', response.data.bookingToolDetail.primaryColorCode);
          }
          if (response.data.bookingToolDetail.primaryTextColorCode) {
            root.style.setProperty('--primary-text-color-code', response.data.bookingToolDetail.primaryTextColorCode);
            root.style.setProperty('--bs-heading-color', response.data.bookingToolDetail.primaryTextColorCode);
          }
        }

        // Load Stripe if available


        if (tripId) {
          getTripDetail(tripId);
          socketService.listen(tripId, () => {
            if (tripId) { }
            getTripDetail(tripId);
          });
        }

        if (tripId2) {
          getTripDetail(tripId2);
          socketService.listen(tripId2, () => {
            getTripDetail(tripId2);
          });
        }
        console.log("hello2");
        // (window as any).gtag_report_conversion();
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const getTripDetail = async (tripIdParam: string) => {
    try {
      const response = await apiService.getData(
        environment['Booking Tool'].tripDetails,
        { tripId: tripIdParam }
      );
      if (response.data && response.data.trip) {
        const trip = response.data.trip;
        if (tripId === tripIdParam && !summaryTracked.current) {
          summaryTracked.current = true;
          trackingService.summaryPageView(trip.brandSlug, trip.gaClientId);
        }

        setTripDetail(prev => {
          const updated = { ...prev };

          if (tripId === tripIdParam) {
            // First trip
            updated.pickup = trip.stops[0];
            updated.drop = trip.stops[1];
            updated.user = trip.user;
            updated.uniqueId = trip.uniqueId;
            updated.country = trip.country;
            updated.timezone = trip.timezone || 'Europe/Amsterdam';
            updated.origin = trip.origin;
            updated.note = trip.note;
            updated.vehicle = trip.vehicle;
            updated.driver = trip.driver || null;
            updated.stops = trip.stops || [];
            updated.direction = trip.direction || null;
            updated.pickupTime = trip.pickupTime ? new Date(trip.pickupTime) : null;
            updated.total1 = trip.invoice.total;
            updated.isPaid1 = trip.invoice.isPaid;
            updated.is_trip_cancelled1 = [11, 12, 13, 14, 15, 16, 17, 18, 19].includes(trip.currentState) ? 1 : 0;
            updated.is_trip_completed1 = [10].includes(trip.currentState) ? 1 : 0;
            updated.is_provider_accepted1 = [2, 3, 4, 5, 6, 7, 8].includes(trip.currentState) ? 1 : 0;
          } else if (tripId2 === tripIdParam) {
            // Return trip
            updated.driver2 = trip.driver || null;
            updated.returnPickupTime = trip.pickupTime ? new Date(trip.pickupTime) : null;
            updated.total2 = trip.invoice.total;
            updated.isPaid2 = trip.invoice.isPaid;
            updated.uniqueId2 = trip.uniqueId;
            updated.is_trip_cancelled2 = [11, 12, 13, 14, 15, 16, 17, 18, 19].includes(trip.currentState) ? 1 : 0;
            updated.is_trip_completed2 = [10].includes(trip.currentState) ? 1 : 0;
            updated.is_provider_accepted2 = [2, 3, 4, 5, 6, 7, 8].includes(trip.currentState) ? 1 : 0;
          }
          // Calculate combined status for return trips
          if (returnTrip.current && updated.total1 && updated.total2) {
            updated.is_trip_completed = ((updated.is_trip_completed1 === 1) && (updated.is_trip_completed2 === 1)) ? 1 : 0;
            updated.is_trip_cancelled = ((updated.is_trip_cancelled1 === 1) && (updated.is_trip_cancelled2 === 1)) ? 1 : 0;
            updated.is_provider_accepted = ((updated.is_provider_accepted1 === 1) && (updated.is_provider_accepted2 === 1)) ? 1 : 0;
            updated.isPaid = ((updated.isPaid1 === 1) && (updated.isPaid2 === 1)) ? 1 : 0;
            updated.invoice = {
              totalWithoutVat: apiService.roundFun((updated.total1 + updated.total2) - ((updated.total1 + updated.total2) * 0.09)),
              vat: apiService.roundFun((updated.total1 + updated.total2) * 0.09),
              total: (updated.total1 + updated.total2),
              paymentMode: trip.invoice.paymentMode,
            };
          } else {
            updated.is_trip_completed = updated.is_trip_completed1 || 0;
            updated.is_trip_cancelled = updated.is_trip_cancelled1 || 0;
            updated.is_provider_accepted = updated.is_provider_accepted1 || 0;
            updated.isPaid = updated.isPaid1 || 0;
            updated.invoice = {
              totalWithoutVat: apiService.roundFun(trip.invoice.total - (trip.invoice.total * 0.09)),
              vat: apiService.roundFun(trip.invoice.total * 0.09),
              total: trip.invoice.total,
              paymentMode: trip.invoice.paymentMode,
            };
          }
          setTimeout(() => {
            renderAutoCard(updated)
          }, 3000);
          return updated;
        });

      }
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching trip details:', error);
      setIsLoading(false);
    }
  };
  const renderAutoCard = (tripDetail: any) => {
    if (tripDetail && (tripDetail.is_provider_accepted || paymentBeforeAccept.current) && !tripDetail.is_trip_cancelled && tripDetail.isPaid === 0 && tripDetail.is_trip_completed === 0 && !mollie && stripePublicKey.current &&
      (paymentMethod.current?.card?.available ||
        paymentMethod.current?.ideal?.available)) {

      setCard(stripe.current);
    }
  }
  const loadStripe = (apiKey: string, pm: any) => {
    const StripeLib = (window as any).Stripe;
    let stripeInstance: any;
    if (StripeLib) {
      stripeInstance = StripeLib(apiKey);
      stripe.current = stripeInstance
    } else {
      // Load Stripe script dynamically
      const script = document.createElement('script');
      script.src = 'https://js.stripe.com/v3/';
      script.onload = () => {
        const StripeLibLoaded = (window as any).Stripe;
        if (StripeLibLoaded) {
          stripeInstance = StripeLibLoaded(apiKey);
          stripe.current = stripeInstance
        }
      };
      document.body.appendChild(script);
    }
    /* setTimeout(() => {
      console.log(pm)
      if (pm?.card?.available){
        setCard(stripeInstance);
      } else if (pm?.ideal?.available) {
        setIdeal(stripeInstance);
      }
    }, 2000); */
  };
  const setCard = (stripeInstance: any) => {
    if (!stripeInstance) return;
    setTripDetail(prev => ({
      ...prev,
      invoice: { ...prev.invoice, paymentMode: 0 }
    }));

    // Clear existing elements
    if (cardElement) {
      cardElement.unmount();
    }
    if (idealElement) {
      idealElement.unmount();
    }

    const elements = stripeInstance.elements();
    const card = elements.create('card');
    card.mount('#card-element');
    setCardElement(card);
  };

  const setIdeal = (stripeInstance: any) => {
    if (!stripeInstance) return;
    setTripDetail(prev => ({
      ...prev,
      invoice: { ...prev.invoice, paymentMode: 3 }
    }));

    // Clear existing elements
    if (cardElement) {
      cardElement.unmount();
    }
    if (idealElement) {
      idealElement.unmount();
    }

    const elements = stripeInstance.elements();
    const ideal = elements.create('idealBank');
    ideal.mount('#ideal-bank-element');
    setIdealElement(ideal);
  };

  const submitPayment = async () => {
    setIsLoading(true);
    if (tripDetail.invoice.paymentMode === 1) {
      await completePayment();
    } else {
      await requestPayment();
    }
  };

  const requestPayment = async () => {
    try {
      const reqData = {
        coid: coid,
        cuid: cuid,
        tripId: tripId,
        tripId2: tripId2 || '',
        redirect_url: window.location.href,
        name: tripDetail.user.name,
        uniqueId: tripDetail.uniqueId,
        uniqueId2: tripDetail.uniqueId2,
        email: tripDetail.user.email,
        price: tripDetail.invoice.total
      };

      const response = await apiService.postOrderData(
        environment['Booking Tool'].requestPayment,
        reqData
      );

      if (response) {
        if (tripDetail.invoice.paymentMode === 0) {
          // Card payment
          if (mollie) {
            window.location.href = response.data.paymentIntent._links.checkout.href;
          } else if (stripe.current && cardElement) {
            const result = await stripe.current.handleCardPayment(
              response.data.paymentIntent.client_secret,
              cardElement,
              {
                payment_method_data: {
                  billing_details: { name: tripDetail.user.name }
                }
              }
            );

            if (result.error) {
              setIsLoading(false);
              alert('Payment Failed: ' + result.error.message);
            } else {
              await completePayment(result);
            }
          }
        } else if (tripDetail.invoice.paymentMode === 3) {
          // iDeal payment
          if (stripe.current && idealElement) {
            await stripe.current.confirmIdealPayment(
              response.data.paymentIntent.client_secret,
              {
                payment_method: {
                  ideal: idealElement,
                  billing_details: {
                    name: tripDetail.user.name
                  }
                },
                return_url: `${environment.API_URL}${environment['Booking Tool'].complete}?paymentMode=3&coid=${coid}&cuid=${cuid}&tripId=${tripId}`
              }
            );
          }
        }
      }
    } catch (error) {
      console.error('Error requesting payment:', error);
      setIsLoading(false);
    }
  };

  const completePayment = async (result?: any) => {
    try {
      await apiService.getData(
        environment['Booking Tool'].complete,
        {
          coid: coid,
          cuid: cuid,
          paymentIntent: result?.paymentIntent?.id || '',
          tripId: tripId,
          paymentMode: tripDetail.invoice.paymentMode
        }
      );
      setIsLoading(false);
      // Refresh trip details
      if (tripId) {
        await getTripDetail(tripId);
      }
      if (tripId2) {
        await getTripDetail(tripId2);
      }
    } catch (error) {
      console.error('Error completing payment:', error);
      setIsLoading(false);
    }
  };

  const cancelTrip = async () => {
    setIsLoading(true);
    try {
      const reqData = {
        currentState: 11,
        tripId: tripId,
      };

      const response = await apiService.postOrderData(
        environment['Booking Tool'].cancel,
        reqData
      );

      if (response.success) {
        if (tripId2) {
          const reqData2 = {
            currentState: 11,
            tripId: tripId2,
          };
          await apiService.postOrderData(
            environment['Booking Tool'].cancel,
            reqData2
          );
        }
        // Refresh trip details
        if (tripId) {
          await getTripDetail(tripId);
        }
        if (tripId2) {
          await getTripDetail(tripId2);
        }
      }
      setIsLoading(false);
    } catch (error) {
      console.error('Error cancelling trip:', error);
      setIsLoading(false);
    }
  };

  const returnToHome = () => {
    if (redirectUrl) {
      window.location.href = redirectUrl;
    }
  };

  const switchLanguage = (lang: any) => {
    setSelectedLanguage(lang);
    apiService.selectedLanguage = lang;
    i18n.changeLanguage(lang.value);
    localStorage.setItem('languageCode', lang.value);
  };

  return (
    <div className="booking-main">
      <div className="booking-contant">
        <div className="container-new">
          <div className="d-flex align-items-center justify-content-between mb-4">
            <div className="language-changes position-relative">
              <Select
                value={selectedLanguage}
                onChange={switchLanguage}
                options={languages}
                isSearchable={false}
                formatOptionLabel={(option: any) => (
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <img src={option.img} alt="" style={{ width: '20px', marginRight: '8px' }} />
                    {t(option.label)}
                  </div>
                )}
              />
            </div>
          </div>

          <div className="py-2">
            <div className="row">
              {/* Transfer Summary Card - Left Side */}
              <div className="col-lg-5 col-xl-5">
                <div className="card h-100">
                  <div className="card-header">
                    <h5 className="mb-0">{t('heading-title.transfer-summary')} - {tripDetail.uniqueId} {tripDetail.uniqueId2? ' ,'+tripDetail.uniqueId2:''}</h5>
                  </div>
                  <div className="card-body summary">
                    <div className="d-flex flex-column py-3 justify-content-between h-100">
                      <div className="d-flex align-items-center justify-content-between mb-5">
                        <h4 className="mb-0">{t('label-title.total-price')}</h4>
                        <h4 className="mb-0 text-nowrap">
                          {tripDetail.country.currencySign} {apiService.roundFun(tripDetail.invoice.total)}
                        </h4>
                      </div>

                      {/* Status Icons */}
                      {(!tripDetail.is_trip_cancelled && tripDetail.isPaid === 1) && (
                        <div className="text-center mb-2 w-100">
                          <svg width="84" height="84" viewBox="0 0 84 84" fill="none">
                            <path d="M33.95 39.55C32.55 38.15 30.45 38.15 29.05 39.55C27.65 40.95 27.65 43.05 29.05 44.45L39.55 54.95C40.25 55.65 40.95 56 42 56C43.05 56 43.75 55.65 44.45 54.95L68.95 26.95C70 25.2 70 23.1 68.25 22.05C66.85 21 64.75 21 63.7 22.4L42 47.25L33.95 39.55Z" fill="var(--theme-color-code)" />
                            <path d="M73.5 38.5C71.4 38.5 70 39.9 70 42C70 57.4 57.4 70 42 70C26.6 70 14 57.4 14 42C14 34.65 16.8 27.65 22.05 22.4C27.3 16.8 34.3 14 42 14C44.1 14 46.55 14.35 48.65 14.7C50.4 15.4 52.5 14.35 53.2 12.25C53.9 10.15 52.5 8.75 50.75 8.05H50.4C47.6 7.35 44.8 7 42 7C22.75 7 7 22.75 7 42.35C7 51.45 10.85 60.55 17.15 66.85C23.8 73.5 32.55 77 41.65 77C60.9 77 76.65 61.25 76.65 42C77 39.9 75.25 38.5 73.5 38.5Z" fill="var(--theme-color-code)" />
                          </svg>
                           {(tripDetail.invoice.paymentMode === 1) && (
                              <h4 className="text-center mt-3">{t('label-title.thank-you-note-completed1')}</h4>
                            )}
                           {(tripDetail.invoice.paymentMode !== 1) && (
                              <h4 className="text-center mt-3">{t('label-title.thank-you-note-completed')}</h4>
                            )}
                        </div>
                      )}

                      {(tripDetail.is_trip_cancelled == 0 && tripDetail.is_provider_accepted == 0) && (
                        <div className="text-center mb-2 w-100">
                          <h4 className="text-center mt-3">{t('label-title.waiting-for-driver')}</h4>
                        </div>
                      )}

                      {(tripDetail.is_trip_cancelled == 1) && (
                        <div className="text-center mb-2 w-100">
                          <h4 className="text-center mt-3">{t('label-title.thank-you-note-cancelled')}</h4>
                        </div>
                      )}

                      {/* Payment Methods */}

                      {(tripDetail.is_trip_cancelled == 0 && (tripDetail.is_provider_accepted == 1 || paymentBeforeAccept.current) && tripDetail.isPaid == 0) && (
                        <div className="cards-list d-flex align-items-center mb-2">
                          {mollie && (
                            <span
                              className={`credit-card mb-2 cursor-pointer ${tripDetail.invoice.paymentMode === 0 ? 'active' : ''}`}
                              onClick={() => setTripDetail(prev => ({ ...prev, invoice: { ...prev.invoice, paymentMode: 0 } }))}
                            >
                              Card
                            </span>
                          )}
                          {!mollie && (
                            <>
                              {stripePublicKey && paymentMethod.current?.ideal?.available && (
                                <span
                                  className={`credit-card mb-2 cursor-pointer ${tripDetail.invoice.paymentMode === 3 ? 'active' : ''}`}
                                  onClick={() => setIdeal(stripe.current)}
                                >
                                  iDeal
                                </span>
                              )}
                              {stripePublicKey && paymentMethod.current?.card?.available && (
                                <span
                                  className={`credit-card mb-2 cursor-pointer ${tripDetail.invoice.paymentMode === 0 ? 'active' : ''}`}
                                  onClick={() => setCard(stripe.current)}
                                >
                                  Card
                                </span>
                              )}
                            </>
                          )}
                          {paymentMethod.current?.cash?.available && (
                            <span
                              className={`credit-card mb-2 cursor-pointer ${tripDetail.invoice.paymentMode === 1 ? 'active' : ''}`}
                              onClick={() => setTripDetail(prev => ({ ...prev, invoice: { ...prev.invoice, paymentMode: 1 } }))}
                            >
                              Cash
                            </span>
                          )}
                        </div>
                      )}

                      {/* Stripe Elements */}
                      {!tripDetail.is_trip_cancelled && (tripDetail.is_provider_accepted === 1 || paymentBeforeAccept.current) && tripDetail.isPaid === 0 && !mollie && (
                        <>
                          <div
                            id="ideal-bank-element"
                            className="form-control mb-3"
                            style={{ display: tripDetail.invoice.paymentMode === 3 ? 'block' : 'none' }}
                          />
                          <div
                            id="card-element"
                            className="mb-3"
                            style={{ display: tripDetail.invoice.paymentMode === 0 ? 'block' : 'none' }}
                          />
                          <div
                            id="card-errors"
                            role="alert"
                            style={{ display: tripDetail.invoice.paymentMode === 0 ? 'block' : 'none' }}
                          />
                        </>
                      )}

                      {/* Payment Button */}
                      {!tripDetail.is_trip_cancelled && (tripDetail.is_provider_accepted === 1 || paymentBeforeAccept.current) && tripDetail.isPaid === 0 && (
                        <div className="book text-center">
                          <button
                            className="btn primary-btn"
                            type="button"
                            onClick={submitPayment}
                            disabled={isLoading}
                          >
                            {t('button-title.checkout')}
                            {isLoading && (
                              <img
                                src="/assets/images/loading.gif"
                                alt="Loading"
                                height="10"
                                width="10"
                                className="m-2"
                              />
                            )}
                          </button>
                        </div>
                      )}

                      {/* Cancel Button */}
                      {!tripDetail.is_trip_cancelled && tripDetail.is_trip_completed === 0 && tripDetail.is_provider_accepted === 0 && (
                        <div className="book text-center">
                          <button
                            className="btn primary-btn"
                            type="button"
                            onClick={cancelTrip}
                            disabled={isLoading}
                          >
                            {t('button-title.cancel')}
                            {isLoading && (
                              <img
                                src="/assets/images/loading.gif"
                                alt="Loading"
                                height="10"
                                width="10"
                                className="m-2"
                              />
                            )}
                          </button>
                        </div>
                      )}

                      {/* Return Home Button */}
                      {tripDetail.is_trip_cancelled === 1 && tripDetail.origin === 'Booking Tool' && (
                        <div className="book text-center">
                          <button
                            className="btn primary-btn"
                            type="button"
                            onClick={returnToHome}
                          >
                            {t('button-title.return-home')}
                          </button>
                        </div>
                      )}

                      {/* Invoice Breakdown */}
                      <div>
                        <div className="d-flex align-items-center pb-3 justify-content-between">
                          <p className="mb-0">{t('label-title.excluding-vat')}</p>
                          <p className="mb-0 price text-nowrap">
                            {tripDetail.country.currencySign} {tripDetail.invoice.totalWithoutVat}
                          </p>
                        </div>
                        <div className="d-flex align-items-center py-2 justify-content-between">
                          <p className="mb-0">{t('label-title.vat')}</p>
                          <p className="mb-0 price text-nowrap">
                            {tripDetail.country.currencySign} {tripDetail.invoice.vat}
                          </p>
                        </div>
                        <div className="d-flex align-items-center pt-3 mb-5 justify-content-between">
                          <p className="mb-0">{t('label-title.including-vat')}</p>
                          <p className="mb-0 price text-nowrap">
                            {tripDetail.country.currencySign} {apiService.roundFun(tripDetail.invoice.total)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Side - Driver and Passenger Details */}
              <div className="col-lg-7 col-xl-7 mb-5 mb-lg-0">
                <div className="row">
                  {/* Driver Details */}
                  <div className={`mt-3 mb-2 ${returnTrip.current ? 'col-md-4' : 'col-md-6'}`}>
                    <h4 className="mb-3">{t('heading-title.driver-details')}
                      {tripDetail.driver && (<button
                        className="btn primary-btn ms-2 btn-sm"
                        type="button"
                        onClick={() => setShowTracking(true)}
                      >
                        {t('button-title.tracking', 'Tracking')}
                      </button>)}
                    </h4>
                    <div className="card h100">
                      <div className="card-body">
                        {!tripDetail.driver ? (
                          <p>{t('label-title.waiting-for-driver')}.</p>
                        ) : (
                          <>
                            <div className="mb-4">
                              <p className="from mb-3">
                                <svg width="10" height="11" viewBox="0 0 10 11" fill="none" style={{ marginTop: '-2px' }}
                                  xmlns="http://www.w3.org/2000/svg">
                                  <path
                                    d="M5 0.85C3.5954 0.85 2.45 1.98011 2.45 3.37305C2.45 4.76598 3.5954 5.89609 5 5.89609C6.4046 5.89609 7.55 4.76598 7.55 3.37305C7.55 1.98011 6.4046 0.85 5 0.85ZM5 5.06875C4.05196 5.06875 3.28333 4.30633 3.28333 3.37305C3.28333 2.43976 4.05196 1.67734 5 1.67734C5.94804 1.67734 6.71667 2.43976 6.71667 3.37305C6.71667 4.30633 5.94804 5.06875 5 5.06875ZM8.09234 7.19054C7.40735 6.50283 6.49814 6.12344 5.53333 6.12344H4.46667C3.50188 6.12344 2.59266 6.50283 1.90766 7.19054C1.22614 7.87476 0.85 8.77919 0.85 9.73633C0.85 9.96638 1.03815 10.15 1.26667 10.15H8.73333C8.96185 10.15 9.15 9.96638 9.15 9.73633C9.15 8.77919 8.77386 7.87476 8.09234 7.19054ZM8.28606 9.32266H1.71394C1.91413 7.98216 3.07261 6.95078 4.46667 6.95078H5.53333C6.92739 6.95078 8.08587 7.98216 8.28606 9.32266Z"
                                    fill="black" stroke="#FBFBFB" strokeWidth="0.3" />
                                </svg>
                                {t('label-title.name')}
                              </p>
                              <h5 className="mb-0">{tripDetail.driver.name}</h5>
                            </div>
                            <div className="mb-4">
                              <p className="from mb-3">
                                <svg width="26" height="13" viewBox="0 0 26 13" fill="none" style={{ marginTop: '-2px' }}
                                  xmlns="http://www.w3.org/2000/svg">
                                  <path
                                    d="M6.09375 10.2344C6.4303 10.2344 6.70312 9.96155 6.70312 9.625C6.70312 9.28845 6.4303 9.01562 6.09375 9.01562C5.7572 9.01562 5.48438 9.28845 5.48438 9.625C5.48438 9.96155 5.7572 10.2344 6.09375 10.2344Z"
                                    fill="#A49191" />
                                  <path
                                    d="M20.7188 10.2344C21.0553 10.2344 21.3281 9.96155 21.3281 9.625C21.3281 9.28845 21.0553 9.01562 20.7188 9.01562C20.3822 9.01562 20.1094 9.28845 20.1094 9.625C20.1094 9.96155 20.3822 10.2344 20.7188 10.2344Z"
                                    fill="#A49191" />
                                  <path
                                    d="M24.243 4.80262L19.7575 4.12215L15.4618 0.58696C15.0617 0.258507 14.5602 0.0787147 14.0425 0.0781318H8.09352C7.75827 0.0773042 7.42718 0.152309 7.12502 0.297532C6.82286 0.442755 6.55746 0.654436 6.34867 0.916733L3.76959 4.14063H2.03125C1.54657 4.14117 1.08189 4.33395 0.739164 4.67667C0.396441 5.01939 0.203663 5.48407 0.203125 5.96876V8.00001C0.203784 8.5924 0.439402 9.16034 0.858286 9.57922C1.27717 9.99811 1.84511 10.2337 2.4375 10.2344H3.52488C3.66192 10.8127 3.9901 11.3278 4.45629 11.6965C4.92248 12.0651 5.49942 12.2656 6.09375 12.2656C6.68808 12.2656 7.26502 12.0651 7.73121 11.6965C8.19741 11.3278 8.52558 10.8127 8.66262 10.2344H18.1499C18.2869 10.8127 18.6151 11.3278 19.0813 11.6965C19.5475 12.0651 20.1244 12.2656 20.7188 12.2656C21.3131 12.2656 21.89 12.0651 22.3562 11.6965C22.8224 11.3278 23.1506 10.8127 23.2876 10.2344H23.9688C24.4534 10.2338 24.9181 10.0411 25.2608 9.69834C25.6036 9.35562 25.7963 8.89094 25.7969 8.40626V6.61012C25.7991 6.17219 25.6431 5.74819 25.3576 5.4161C25.0721 5.08401 24.6763 4.86616 24.243 4.80262ZM14.6877 1.52824L17.8622 4.14063H11.9844V1.29688H14.0425C14.2779 1.2972 14.5058 1.37894 14.6877 1.52824ZM7.30041 1.67774C7.39535 1.55858 7.516 1.46242 7.65334 1.39647C7.79068 1.33052 7.94116 1.29647 8.09352 1.29688H8.32812V4.14063H5.33036L7.30041 1.67774ZM6.09375 11.0469C5.81253 11.0469 5.53762 10.9635 5.3038 10.8073C5.06997 10.651 4.88773 10.4289 4.78011 10.1691C4.67249 9.90932 4.64433 9.62343 4.6992 9.34761C4.75406 9.0718 4.88948 8.81844 5.08833 8.61959C5.28719 8.42074 5.54054 8.28532 5.81636 8.23045C6.09217 8.17559 6.37806 8.20375 6.63788 8.31137C6.89769 8.41898 7.11976 8.60123 7.276 8.83506C7.43223 9.06888 7.51562 9.34379 7.51562 9.62501C7.51519 10.002 7.36525 10.3634 7.09869 10.6299C6.83213 10.8965 6.47072 11.0465 6.09375 11.0469ZM20.7188 11.0469C20.4375 11.0469 20.1626 10.9635 19.9288 10.8073C19.695 10.651 19.5127 10.4289 19.4051 10.1691C19.2975 9.90932 19.2693 9.62343 19.3242 9.34761C19.3791 9.0718 19.5145 8.81844 19.7133 8.61959C19.9122 8.42074 20.1655 8.28532 20.4414 8.23045C20.7172 8.17559 21.0031 8.20375 21.2629 8.31137C21.5227 8.41898 21.7448 8.60123 21.901 8.83506C22.0572 9.06888 22.1406 9.34379 22.1406 9.62501C22.1402 10.002 21.9903 10.3634 21.7237 10.6299C21.4571 10.8965 21.0957 11.0465 20.7188 11.0469ZM24.5781 8.40626C24.5779 8.56782 24.5137 8.72271 24.3995 8.83696C24.2852 8.9512 24.1303 9.01546 23.9688 9.01563H23.2876C23.1506 8.43732 22.8224 7.92217 22.3562 7.55354C21.89 7.18491 21.3131 6.98437 20.7188 6.98437C20.1244 6.98437 19.5475 7.18491 19.0813 7.55354C18.6151 7.92217 18.2869 8.43732 18.1499 9.01563H8.66262C8.52558 8.43732 8.19741 7.92217 7.73121 7.55354C7.26502 7.18491 6.68808 6.98437 6.09375 6.98437C5.49942 6.98437 4.92248 7.18491 4.45629 7.55354C3.9901 7.92217 3.66192 8.43732 3.52488 9.01563H2.4375C2.16823 9.01532 1.91009 8.90822 1.71969 8.71782C1.52929 8.52742 1.42218 8.26927 1.42188 8.00001V5.96876C1.42205 5.80719 1.48631 5.6523 1.60055 5.53806C1.71479 5.42381 1.86969 5.35956 2.03125 5.35938H8.9375C9.09912 5.35938 9.25411 5.29518 9.36839 5.1809C9.48267 5.06662 9.54688 4.91162 9.54688 4.75001V1.29688H10.7656V4.75001C10.7656 4.91162 10.8298 5.06662 10.9441 5.1809C11.0584 5.29518 11.2134 5.35938 11.375 5.35938H19.7886L24.0602 6.00755C24.2039 6.03022 24.335 6.10327 24.4299 6.21365C24.5248 6.32404 24.5773 6.46456 24.5781 6.61012V8.40626Z"
                                    fill="#A49191" />
                                  <path
                                    d="M12.1875 5.76562H11.375C11.2134 5.76562 11.0584 5.82983 10.9441 5.94411C10.8298 6.05839 10.7656 6.21338 10.7656 6.375C10.7656 6.53662 10.8298 6.69161 10.9441 6.80589C11.0584 6.92017 11.2134 6.98438 11.375 6.98438H12.1875C12.3491 6.98438 12.5041 6.92017 12.6184 6.80589C12.7327 6.69161 12.7969 6.53662 12.7969 6.375C12.7969 6.21338 12.7327 6.05839 12.6184 5.94411C12.5041 5.82983 12.3491 5.76562 12.1875 5.76562Z"
                                    fill="#A49191" />
                                  <path
                                    d="M6.09375 10.2344C6.4303 10.2344 6.70312 9.96155 6.70312 9.625C6.70312 9.28845 6.4303 9.01562 6.09375 9.01562C5.7572 9.01562 5.48438 9.28845 5.48438 9.625C5.48438 9.96155 5.7572 10.2344 6.09375 10.2344Z"
                                    fill="#A49191" />
                                  <path
                                    d="M20.7188 10.2344C21.0553 10.2344 21.3281 9.96155 21.3281 9.625C21.3281 9.28845 21.0553 9.01562 20.7188 9.01562C20.3822 9.01562 20.1094 9.28845 20.1094 9.625C20.1094 9.96155 20.3822 10.2344 20.7188 10.2344Z"
                                    fill="#A49191" />
                                </svg>
                                {t('label-title.car-type')}
                              </p>
                              <h5 className="mb-0">{tripDetail.driver.plateNo} ({tripDetail.driver.model})</h5>
                            </div>
                            <div>
                              <p className="from mb-3">
                                <svg width="13" height="13" viewBox="0 0 13 13" fill="none" style={{ marginTop: '-2px' }}
                                  xmlns="http://www.w3.org/2000/svg">
                                  <path
                                    d="M10.2744 8.05303C10.0083 7.77592 9.68725 7.62775 9.34702 7.62775C9.00955 7.62775 8.68578 7.77317 8.40867 8.05029L7.54164 8.91457C7.47031 8.87616 7.39897 8.84049 7.33038 8.80482C7.2316 8.75543 7.13832 8.70879 7.05875 8.6594C6.2466 8.14358 5.50853 7.47136 4.80065 6.6016C4.45768 6.16809 4.22721 5.80317 4.05984 5.43276C4.28483 5.22698 4.49335 5.01297 4.69639 4.80719C4.77321 4.73037 4.85004 4.6508 4.92686 4.57397C5.50305 3.99779 5.50305 3.25149 4.92686 2.67531L4.17782 1.92626C4.09276 1.84121 4.00496 1.75341 3.92265 1.66561C3.75803 1.4955 3.58517 1.3199 3.40683 1.15527C3.14069 0.891875 2.82241 0.751944 2.48768 0.751944C2.15294 0.751944 1.82918 0.891875 1.55481 1.15527L1.54932 1.16076L0.616447 2.10186C0.265248 2.45306 0.0649555 2.88109 0.0210557 3.3777C-0.044794 4.17887 0.191168 4.92517 0.372254 5.41356C0.81674 6.61257 1.48072 7.72379 2.47121 8.91457C3.67297 10.3495 5.11892 11.4827 6.77065 12.2811C7.40171 12.5802 8.24404 12.9341 9.18514 12.9945C9.24276 12.9973 9.30313 13 9.358 13C9.9918 13 10.5241 12.7723 10.9411 12.3195C10.9439 12.3141 10.9494 12.3113 10.9521 12.3058C11.0948 12.133 11.2594 11.9766 11.4323 11.8092C11.5502 11.6967 11.671 11.5787 11.789 11.4553C12.0606 11.1727 12.2033 10.8434 12.2033 10.5059C12.2033 10.1657 12.0578 9.83921 11.7807 9.56483L10.2744 8.05303ZM11.2567 10.9422C11.2539 10.9422 11.2539 10.9449 11.2567 10.9422C11.1497 11.0574 11.0399 11.1617 10.9219 11.2769C10.7436 11.447 10.5625 11.6254 10.3924 11.8257C10.1153 12.122 9.78877 12.2619 9.36074 12.2619C9.31959 12.2619 9.27569 12.2619 9.23453 12.2592C8.41964 12.2071 7.66237 11.8888 7.09442 11.6172C5.54146 10.8654 4.17782 9.79805 3.04466 8.44539C2.10904 7.31771 1.48347 6.27509 1.06916 5.15565C0.813996 4.47245 0.720709 3.94017 0.761865 3.43806C0.789303 3.11705 0.912771 2.8509 1.1405 2.62317L2.07612 1.68756C2.21056 1.56135 2.35323 1.49275 2.49316 1.49275C2.66602 1.49275 2.80595 1.59702 2.89375 1.68482L2.90198 1.69305C3.06935 1.84944 3.22849 2.01132 3.39585 2.18418C3.48091 2.27198 3.56871 2.35978 3.65651 2.45032L4.40555 3.19936C4.69639 3.4902 4.69639 3.75908 4.40555 4.04992C4.32598 4.12949 4.24916 4.20905 4.16959 4.28588C3.93912 4.52184 3.71962 4.74134 3.48091 4.95535C3.47542 4.96084 3.46994 4.96358 3.46719 4.96907C3.23123 5.20503 3.27513 5.43551 3.32452 5.5919L3.33275 5.61659C3.52755 6.08852 3.80193 6.533 4.21898 7.06254L4.22172 7.06529C4.97899 7.99816 5.77742 8.72525 6.65816 9.28223C6.77065 9.35357 6.88589 9.41119 6.99564 9.46606C7.09441 9.51545 7.1877 9.56209 7.26727 9.61148C7.27825 9.61697 7.28922 9.6252 7.3002 9.63068C7.39348 9.67733 7.48128 9.69928 7.57183 9.69928C7.79956 9.69928 7.94223 9.5566 7.98887 9.50996L8.92723 8.5716C9.02052 8.47831 9.16868 8.36582 9.34154 8.36582C9.51165 8.36582 9.65158 8.47283 9.73664 8.56611L9.74212 8.5716L11.2539 10.0834C11.5365 10.3633 11.5365 10.6514 11.2567 10.9422ZM7.02582 3.09235C7.74468 3.21308 8.39769 3.5533 8.919 4.07461C9.44031 4.59592 9.77779 5.24893 9.90126 5.96779C9.93144 6.14888 10.0878 6.27509 10.2662 6.27509C10.2881 6.27509 10.3073 6.27235 10.3293 6.2696C10.5323 6.23668 10.6668 6.04462 10.6338 5.84158C10.4857 4.97182 10.0741 4.17887 9.4458 3.55056C8.81748 2.92224 8.02454 2.51068 7.15478 2.36252C6.95174 2.32959 6.76242 2.46404 6.72675 2.66433C6.69109 2.86462 6.82278 3.05943 7.02582 3.09235ZM12.9852 5.73457C12.741 4.30234 12.0661 2.99907 11.0289 1.96193C9.9918 0.9248 8.68853 0.24984 7.2563 0.00564702C7.056 -0.0300216 6.86668 0.107165 6.83102 0.307458C6.79809 0.510495 6.93253 0.699813 7.13557 0.735482C8.41415 0.952237 9.58024 1.5586 10.5076 2.48324C11.435 3.41063 12.0386 4.57672 12.2554 5.8553C12.2856 6.03639 12.442 6.1626 12.6203 6.1626C12.6423 6.1626 12.6615 6.15985 12.6834 6.15711C12.8837 6.12693 13.0209 5.93487 12.9852 5.73457Z"
                                    fill="#A49191" />
                                </svg>
                                {t('label-title.mobile-no')}.
                              </p>
                              <h5 className="mb-0">{tripDetail.driver.phoneCode}{tripDetail.driver.phone}</h5>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Return Trip Driver Details */}
                  {returnTrip.current && (
                    <div className="col-md-4 mt-3 mb-2">
                      <h4 className="mb-3">{t('label-title.return-trip')} {t('heading-title.driver-details')}</h4>
                      <div className="card h100">
                        <div className="card-body">
                          {!tripDetail.driver2 ? (
                            <p>{t('label-title.waiting-for-driver')}.</p>
                          ) : (
                            <>
                              <div className="mb-4">
                                <p className="from mb-3">
                                  <svg width="10" height="11" viewBox="0 0 10 11" fill="none" style={{ marginTop: '-2px' }}
                                    xmlns="http://www.w3.org/2000/svg">
                                    <path
                                      d="M5 0.85C3.5954 0.85 2.45 1.98011 2.45 3.37305C2.45 4.76598 3.5954 5.89609 5 5.89609C6.4046 5.89609 7.55 4.76598 7.55 3.37305C7.55 1.98011 6.4046 0.85 5 0.85ZM5 5.06875C4.05196 5.06875 3.28333 4.30633 3.28333 3.37305C3.28333 2.43976 4.05196 1.67734 5 1.67734C5.94804 1.67734 6.71667 2.43976 6.71667 3.37305C6.71667 4.30633 5.94804 5.06875 5 5.06875ZM8.09234 7.19054C7.40735 6.50283 6.49814 6.12344 5.53333 6.12344H4.46667C3.50188 6.12344 2.59266 6.50283 1.90766 7.19054C1.22614 7.87476 0.85 8.77919 0.85 9.73633C0.85 9.96638 1.03815 10.15 1.26667 10.15H8.73333C8.96185 10.15 9.15 9.96638 9.15 9.73633C9.15 8.77919 8.77386 7.87476 8.09234 7.19054ZM8.28606 9.32266H1.71394C1.91413 7.98216 3.07261 6.95078 4.46667 6.95078H5.53333C6.92739 6.95078 8.08587 7.98216 8.28606 9.32266Z"
                                      fill="black" stroke="#FBFBFB" strokeWidth="0.3" />
                                  </svg>
                                  {t('label-title.name')}
                                </p>
                                <h5 className="mb-0">{tripDetail.driver2.name}</h5>
                              </div>
                              <div className="mb-4">
                                <p className="from mb-3">
                                  <svg width="26" height="13" viewBox="0 0 26 13" fill="none" style={{ marginTop: '-2px' }}
                                    xmlns="http://www.w3.org/2000/svg">
                                    <path
                                      d="M6.09375 10.2344C6.4303 10.2344 6.70312 9.96155 6.70312 9.625C6.70312 9.28845 6.4303 9.01562 6.09375 9.01562C5.7572 9.01562 5.48438 9.28845 5.48438 9.625C5.48438 9.96155 5.7572 10.2344 6.09375 10.2344Z"
                                      fill="#A49191" />
                                    <path
                                      d="M20.7188 10.2344C21.0553 10.2344 21.3281 9.96155 21.3281 9.625C21.3281 9.28845 21.0553 9.01562 20.7188 9.01562C20.3822 9.01562 20.1094 9.28845 20.1094 9.625C20.1094 9.96155 20.3822 10.2344 20.7188 10.2344Z"
                                      fill="#A49191" />
                                    <path
                                      d="M24.243 4.80262L19.7575 4.12215L15.4618 0.58696C15.0617 0.258507 14.5602 0.0787147 14.0425 0.0781318H8.09352C7.75827 0.0773042 7.42718 0.152309 7.12502 0.297532C6.82286 0.442755 6.55746 0.654436 6.34867 0.916733L3.76959 4.14063H2.03125C1.54657 4.14117 1.08189 4.33395 0.739164 4.67667C0.396441 5.01939 0.203663 5.48407 0.203125 5.96876V8.00001C0.203784 8.5924 0.439402 9.16034 0.858286 9.57922C1.27717 9.99811 1.84511 10.2337 2.4375 10.2344H3.52488C3.66192 10.8127 3.9901 11.3278 4.45629 11.6965C4.92248 12.0651 5.49942 12.2656 6.09375 12.2656C6.68808 12.2656 7.26502 12.0651 7.73121 11.6965C8.19741 11.3278 8.52558 10.8127 8.66262 10.2344H18.1499C18.2869 10.8127 18.6151 11.3278 19.0813 11.6965C19.5475 12.0651 20.1244 12.2656 20.7188 12.2656C21.3131 12.2656 21.89 12.0651 22.3562 11.6965C22.8224 11.3278 23.1506 10.8127 23.2876 10.2344H23.9688C24.4534 10.2338 24.9181 10.0411 25.2608 9.69834C25.6036 9.35562 25.7963 8.89094 25.7969 8.40626V6.61012C25.7991 6.17219 25.6431 5.74819 25.3576 5.4161C25.0721 5.08401 24.6763 4.86616 24.243 4.80262ZM14.6877 1.52824L17.8622 4.14063H11.9844V1.29688H14.0425C14.2779 1.2972 14.5058 1.37894 14.6877 1.52824ZM7.30041 1.67774C7.39535 1.55858 7.516 1.46242 7.65334 1.39647C7.79068 1.33052 7.94116 1.29647 8.09352 1.29688H8.32812V4.14063H5.33036L7.30041 1.67774ZM6.09375 11.0469C5.81253 11.0469 5.53762 10.9635 5.3038 10.8073C5.06997 10.651 4.88773 10.4289 4.78011 10.1691C4.67249 9.90932 4.64433 9.62343 4.6992 9.34761C4.75406 9.0718 4.88948 8.81844 5.08833 8.61959C5.28719 8.42074 5.54054 8.28532 5.81636 8.23045C6.09217 8.17559 6.37806 8.20375 6.63788 8.31137C6.89769 8.41898 7.11976 8.60123 7.276 8.83506C7.43223 9.06888 7.51562 9.34379 7.51562 9.62501C7.51519 10.002 7.36525 10.3634 7.09869 10.6299C6.83213 10.8965 6.47072 11.0465 6.09375 11.0469ZM20.7188 11.0469C20.4375 11.0469 20.1626 10.9635 19.9288 10.8073C19.695 10.651 19.5127 10.4289 19.4051 10.1691C19.2975 9.90932 19.2693 9.62343 19.3242 9.34761C19.3791 9.0718 19.5145 8.81844 19.7133 8.61959C19.9122 8.42074 20.1655 8.28532 20.4414 8.23045C20.7172 8.17559 21.0031 8.20375 21.2629 8.31137C21.5227 8.41898 21.7448 8.60123 21.901 8.83506C22.0572 9.06888 22.1406 9.34379 22.1406 9.62501C22.1402 10.002 21.9903 10.3634 21.7237 10.6299C21.4571 10.8965 21.0957 11.0465 20.7188 11.0469ZM24.5781 8.40626C24.5779 8.56782 24.5137 8.72271 24.3995 8.83696C24.2852 8.9512 24.1303 9.01546 23.9688 9.01563H23.2876C23.1506 8.43732 22.8224 7.92217 22.3562 7.55354C21.89 7.18491 21.3131 6.98437 20.7188 6.98437C20.1244 6.98437 19.5475 7.18491 19.0813 7.55354C18.6151 7.92217 18.2869 8.43732 18.1499 9.01563H8.66262C8.52558 8.43732 8.19741 7.92217 7.73121 7.55354C7.26502 7.18491 6.68808 6.98437 6.09375 6.98437C5.49942 6.98437 4.92248 7.18491 4.45629 7.55354C3.9901 7.92217 3.66192 8.43732 3.52488 9.01563H2.4375C2.16823 9.01532 1.91009 8.90822 1.71969 8.71782C1.52929 8.52742 1.42218 8.26927 1.42188 8.00001V5.96876C1.42205 5.80719 1.48631 5.6523 1.60055 5.53806C1.71479 5.42381 1.86969 5.35956 2.03125 5.35938H8.9375C9.09912 5.35938 9.25411 5.29518 9.36839 5.1809C9.48267 5.06662 9.54688 4.91162 9.54688 4.75001V1.29688H10.7656V4.75001C10.7656 4.91162 10.8298 5.06662 10.9441 5.1809C11.0584 5.29518 11.2134 5.35938 11.375 5.35938H19.7886L24.0602 6.00755C24.2039 6.03022 24.335 6.10327 24.4299 6.21365C24.5248 6.32404 24.5773 6.46456 24.5781 6.61012V8.40626Z"
                                      fill="#A49191" />
                                    <path
                                      d="M12.1875 5.76562H11.375C11.2134 5.76562 11.0584 5.82983 10.9441 5.94411C10.8298 6.05839 10.7656 6.21338 10.7656 6.375C10.7656 6.53662 10.8298 6.69161 10.9441 6.80589C11.0584 6.92017 11.2134 6.98438 11.375 6.98438H12.1875C12.3491 6.98438 12.5041 6.92017 12.6184 6.80589C12.7327 6.69161 12.7969 6.53662 12.7969 6.375C12.7969 6.21338 12.7327 6.05839 12.6184 5.94411C12.5041 5.82983 12.3491 5.76562 12.1875 5.76562Z"
                                      fill="#A49191" />
                                    <path
                                      d="M6.09375 10.2344C6.4303 10.2344 6.70312 9.96155 6.70312 9.625C6.70312 9.28845 6.4303 9.01562 6.09375 9.01562C5.7572 9.01562 5.48438 9.28845 5.48438 9.625C5.48438 9.96155 5.7572 10.2344 6.09375 10.2344Z"
                                      fill="#A49191" />
                                    <path
                                      d="M20.7188 10.2344C21.0553 10.2344 21.3281 9.96155 21.3281 9.625C21.3281 9.28845 21.0553 9.01562 20.7188 9.01562C20.3822 9.01562 20.1094 9.28845 20.1094 9.625C20.1094 9.96155 20.3822 10.2344 20.7188 10.2344Z"
                                      fill="#A49191" />
                                  </svg>
                                  {t('label-title.car-type')}
                                </p>
                                <h5 className="mb-0">{tripDetail.driver2.plateNo} ({tripDetail.driver2.model})</h5>
                              </div>
                              <div>
                                <p className="from mb-3">
                                  <svg width="13" height="13" viewBox="0 0 13 13" fill="none" style={{ marginTop: '-2px' }}
                                    xmlns="http://www.w3.org/2000/svg">
                                    <path
                                      d="M10.2744 8.05303C10.0083 7.77592 9.68725 7.62775 9.34702 7.62775C9.00955 7.62775 8.68578 7.77317 8.40867 8.05029L7.54164 8.91457C7.47031 8.87616 7.39897 8.84049 7.33038 8.80482C7.2316 8.75543 7.13832 8.70879 7.05875 8.6594C6.2466 8.14358 5.50853 7.47136 4.80065 6.6016C4.45768 6.16809 4.22721 5.80317 4.05984 5.43276C4.28483 5.22698 4.49335 5.01297 4.69639 4.80719C4.77321 4.73037 4.85004 4.6508 4.92686 4.57397C5.50305 3.99779 5.50305 3.25149 4.92686 2.67531L4.17782 1.92626C4.09276 1.84121 4.00496 1.75341 3.92265 1.66561C3.75803 1.4955 3.58517 1.3199 3.40683 1.15527C3.14069 0.891875 2.82241 0.751944 2.48768 0.751944C2.15294 0.751944 1.82918 0.891875 1.55481 1.15527L1.54932 1.16076L0.616447 2.10186C0.265248 2.45306 0.0649555 2.88109 0.0210557 3.3777C-0.044794 4.17887 0.191168 4.92517 0.372254 5.41356C0.81674 6.61257 1.48072 7.72379 2.47121 8.91457C3.67297 10.3495 5.11892 11.4827 6.77065 12.2811C7.40171 12.5802 8.24404 12.9341 9.18514 12.9945C9.24276 12.9973 9.30313 13 9.358 13C9.9918 13 10.5241 12.7723 10.9411 12.3195C10.9439 12.3141 10.9494 12.3113 10.9521 12.3058C11.0948 12.133 11.2594 11.9766 11.4323 11.8092C11.5502 11.6967 11.671 11.5787 11.789 11.4553C12.0606 11.1727 12.2033 10.8434 12.2033 10.5059C12.2033 10.1657 12.0578 9.83921 11.7807 9.56483L10.2744 8.05303ZM11.2567 10.9422C11.2539 10.9422 11.2539 10.9449 11.2567 10.9422C11.1497 11.0574 11.0399 11.1617 10.9219 11.2769C10.7436 11.447 10.5625 11.6254 10.3924 11.8257C10.1153 12.122 9.78877 12.2619 9.36074 12.2619C9.31959 12.2619 9.27569 12.2619 9.23453 12.2592C8.41964 12.2071 7.66237 11.8888 7.09442 11.6172C5.54146 10.8654 4.17782 9.79805 3.04466 8.44539C2.10904 7.31771 1.48347 6.27509 1.06916 5.15565C0.813996 4.47245 0.720709 3.94017 0.761865 3.43806C0.789303 3.11705 0.912771 2.8509 1.1405 2.62317L2.07612 1.68756C2.21056 1.56135 2.35323 1.49275 2.49316 1.49275C2.66602 1.49275 2.80595 1.59702 2.89375 1.68482L2.90198 1.69305C3.06935 1.84944 3.22849 2.01132 3.39585 2.18418C3.48091 2.27198 3.56871 2.35978 3.65651 2.45032L4.40555 3.19936C4.69639 3.4902 4.69639 3.75908 4.40555 4.04992C4.32598 4.12949 4.24916 4.20905 4.16959 4.28588C3.93912 4.52184 3.71962 4.74134 3.48091 4.95535C3.47542 4.96084 3.46994 4.96358 3.46719 4.96907C3.23123 5.20503 3.27513 5.43551 3.32452 5.5919L3.33275 5.61659C3.52755 6.08852 3.80193 6.533 4.21898 7.06254L4.22172 7.06529C4.97899 7.99816 5.77742 8.72525 6.65816 9.28223C6.77065 9.35357 6.88589 9.41119 6.99564 9.46606C7.09441 9.51545 7.1877 9.56209 7.26727 9.61148C7.27825 9.61697 7.28922 9.6252 7.3002 9.63068C7.39348 9.67733 7.48128 9.69928 7.57183 9.69928C7.79956 9.69928 7.94223 9.5566 7.98887 9.50996L8.92723 8.5716C9.02052 8.47831 9.16868 8.36582 9.34154 8.36582C9.51165 8.36582 9.65158 8.47283 9.73664 8.56611L9.74212 8.5716L11.2539 10.0834C11.5365 10.3633 11.5365 10.6514 11.2567 10.9422ZM7.02582 3.09235C7.74468 3.21308 8.39769 3.5533 8.919 4.07461C9.44031 4.59592 9.77779 5.24893 9.90126 5.96779C9.93144 6.14888 10.0878 6.27509 10.2662 6.27509C10.2881 6.27509 10.3073 6.27235 10.3293 6.2696C10.5323 6.23668 10.6668 6.04462 10.6338 5.84158C10.4857 4.97182 10.0741 4.17887 9.4458 3.55056C8.81748 2.92224 8.02454 2.51068 7.15478 2.36252C6.95174 2.32959 6.76242 2.46404 6.72675 2.66433C6.69109 2.86462 6.82278 3.05943 7.02582 3.09235ZM12.9852 5.73457C12.741 4.30234 12.0661 2.99907 11.0289 1.96193C9.9918 0.9248 8.68853 0.24984 7.2563 0.00564702C7.056 -0.0300216 6.86668 0.107165 6.83102 0.307458C6.79809 0.510495 6.93253 0.699813 7.13557 0.735482C8.41415 0.952237 9.58024 1.5586 10.5076 2.48324C11.435 3.41063 12.0386 4.57672 12.2554 5.8553C12.2856 6.03639 12.442 6.1626 12.6203 6.1626C12.6423 6.1626 12.6615 6.15985 12.6834 6.15711C12.8837 6.12693 13.0209 5.93487 12.9852 5.73457Z"
                                      fill="#A49191" />
                                  </svg>
                                  {t('label-title.mobile-no')}.
                                </p>
                                <h5 className="mb-0">{tripDetail.driver2.phoneCode}{tripDetail.driver2.phone}</h5>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Passenger Details */}
                  <div className={`mt-3 mb-2 ${returnTrip.current ? 'col-md-4' : 'col-md-6'}`}>
                    <h4 className="mb-3">{t('heading-title.passanger-details')}</h4>
                    <div className="card">
                      <div className="card-body">
                        <div className="mb-4">
                          <p className="from mb-3">
                            <svg width="10" height="11" viewBox="0 0 10 11" fill="none" style={{ marginTop: '-2px' }}
                              xmlns="http://www.w3.org/2000/svg">
                              <path
                                d="M5 0.85C3.5954 0.85 2.45 1.98011 2.45 3.37305C2.45 4.76598 3.5954 5.89609 5 5.89609C6.4046 5.89609 7.55 4.76598 7.55 3.37305C7.55 1.98011 6.4046 0.85 5 0.85ZM5 5.06875C4.05196 5.06875 3.28333 4.30633 3.28333 3.37305C3.28333 2.43976 4.05196 1.67734 5 1.67734C5.94804 1.67734 6.71667 2.43976 6.71667 3.37305C6.71667 4.30633 5.94804 5.06875 5 5.06875ZM8.09234 7.19054C7.40735 6.50283 6.49814 6.12344 5.53333 6.12344H4.46667C3.50188 6.12344 2.59266 6.50283 1.90766 7.19054C1.22614 7.87476 0.85 8.77919 0.85 9.73633C0.85 9.96638 1.03815 10.15 1.26667 10.15H8.73333C8.96185 10.15 9.15 9.96638 9.15 9.73633C9.15 8.77919 8.77386 7.87476 8.09234 7.19054ZM8.28606 9.32266H1.71394C1.91413 7.98216 3.07261 6.95078 4.46667 6.95078H5.53333C6.92739 6.95078 8.08587 7.98216 8.28606 9.32266Z"
                                fill="black" stroke="#FBFBFB" strokeWidth="0.3" />
                            </svg>
                            {t('label-title.name')}
                          </p>
                          <h5 className="mb-0">{tripDetail.user.name}</h5>
                        </div>
                        <div className="mb-4">
                          <p className="from mb-3">
                            <svg width="13" height="9" viewBox="0 0 13 9" fill="none" style={{ marginTop: '-2px' }} xmlns="http://www.w3.org/2000/svg">
                              <path
                                d="M11.8574 0H1.14258C0.513856 0 0 0.503725 0 1.125V7.875C0 8.4965 0.514186 9 1.14258 9H11.8574C12.4861 9 13 8.49627 13 7.875V1.125C13 0.503575 12.4859 0 11.8574 0ZM11.682 0.75L6.79514 5.57735C6.64625 5.7244 6.35383 5.7245 6.20486 5.57735L1.31803 0.75H11.682ZM0.761719 7.73712V1.26287L4.03876 4.5L0.761719 7.73712ZM1.31803 8.25L4.57651 5.0312L5.66538 6.10683C6.1116 6.5476 6.88858 6.54743 7.33464 6.10683L8.42352 5.03122L11.682 8.25H1.31803ZM12.2383 7.73712L8.96124 4.5L12.2383 1.26287V7.73712Z"
                                fill="#A49191" />
                            </svg>
                            {t('label-title.email')}
                          </p>
                          <h5 className="mb-0">{tripDetail.user.email}</h5>
                        </div>
                        <div>
                          <p className="from mb-3">
                            <svg width="13" height="13" viewBox="0 0 13 13" fill="none" style={{ marginTop: '-2px' }}
                              xmlns="http://www.w3.org/2000/svg">
                              <path
                                d="M10.2744 8.05303C10.0083 7.77592 9.68725 7.62775 9.34702 7.62775C9.00955 7.62775 8.68578 7.77317 8.40867 8.05029L7.54164 8.91457C7.47031 8.87616 7.39897 8.84049 7.33038 8.80482C7.2316 8.75543 7.13832 8.70879 7.05875 8.6594C6.2466 8.14358 5.50853 7.47136 4.80065 6.6016C4.45768 6.16809 4.22721 5.80317 4.05984 5.43276C4.28483 5.22698 4.49335 5.01297 4.69639 4.80719C4.77321 4.73037 4.85004 4.6508 4.92686 4.57397C5.50305 3.99779 5.50305 3.25149 4.92686 2.67531L4.17782 1.92626C4.09276 1.84121 4.00496 1.75341 3.92265 1.66561C3.75803 1.4955 3.58517 1.3199 3.40683 1.15527C3.14069 0.891875 2.82241 0.751944 2.48768 0.751944C2.15294 0.751944 1.82918 0.891875 1.55481 1.15527L1.54932 1.16076L0.616447 2.10186C0.265248 2.45306 0.0649555 2.88109 0.0210557 3.3777C-0.044794 4.17887 0.191168 4.92517 0.372254 5.41356C0.81674 6.61257 1.48072 7.72379 2.47121 8.91457C3.67297 10.3495 5.11892 11.4827 6.77065 12.2811C7.40171 12.5802 8.24404 12.9341 9.18514 12.9945C9.24276 12.9973 9.30313 13 9.358 13C9.9918 13 10.5241 12.7723 10.9411 12.3195C10.9439 12.3141 10.9494 12.3113 10.9521 12.3058C11.0948 12.133 11.2594 11.9766 11.4323 11.8092C11.5502 11.6967 11.671 11.5787 11.789 11.4553C12.0606 11.1727 12.2033 10.8434 12.2033 10.5059C12.2033 10.1657 12.0578 9.83921 11.7807 9.56483L10.2744 8.05303ZM11.2567 10.9422C11.2539 10.9422 11.2539 10.9449 11.2567 10.9422C11.1497 11.0574 11.0399 11.1617 10.9219 11.2769C10.7436 11.447 10.5625 11.6254 10.3924 11.8257C10.1153 12.122 9.78877 12.2619 9.36074 12.2619C9.31959 12.2619 9.27569 12.2619 9.23453 12.2592C8.41964 12.2071 7.66237 11.8888 7.09442 11.6172C5.54146 10.8654 4.17782 9.79805 3.04466 8.44539C2.10904 7.31771 1.48347 6.27509 1.06916 5.15565C0.813996 4.47245 0.720709 3.94017 0.761865 3.43806C0.789303 3.11705 0.912771 2.8509 1.1405 2.62317L2.07612 1.68756C2.21056 1.56135 2.35323 1.49275 2.49316 1.49275C2.66602 1.49275 2.80595 1.59702 2.89375 1.68482L2.90198 1.69305C3.06935 1.84944 3.22849 2.01132 3.39585 2.18418C3.48091 2.27198 3.56871 2.35978 3.65651 2.45032L4.40555 3.19936C4.69639 3.4902 4.69639 3.75908 4.40555 4.04992C4.32598 4.12949 4.24916 4.20905 4.16959 4.28588C3.93912 4.52184 3.71962 4.74134 3.48091 4.95535C3.47542 4.96084 3.46994 4.96358 3.46719 4.96907C3.23123 5.20503 3.27513 5.43551 3.32452 5.5919L3.33275 5.61659C3.52755 6.08852 3.80193 6.533 4.21898 7.06254L4.22172 7.06529C4.97899 7.99816 5.77742 8.72525 6.65816 9.28223C6.77065 9.35357 6.88589 9.41119 6.99564 9.46606C7.09441 9.51545 7.1877 9.56209 7.26727 9.61148C7.27825 9.61697 7.28922 9.6252 7.3002 9.63068C7.39348 9.67733 7.48128 9.69928 7.57183 9.69928C7.79956 9.69928 7.94223 9.5566 7.98887 9.50996L8.92723 8.5716C9.02052 8.47831 9.16868 8.36582 9.34154 8.36582C9.51165 8.36582 9.65158 8.47283 9.73664 8.56611L9.74212 8.5716L11.2539 10.0834C11.5365 10.3633 11.5365 10.6514 11.2567 10.9422ZM7.02582 3.09235C7.74468 3.21308 8.39769 3.5533 8.919 4.07461C9.44031 4.59592 9.77779 5.24893 9.90126 5.96779C9.93144 6.14888 10.0878 6.27509 10.2662 6.27509C10.2881 6.27509 10.3073 6.27235 10.3293 6.2696C10.5323 6.23668 10.6668 6.04462 10.6338 5.84158C10.4857 4.97182 10.0741 4.17887 9.4458 3.55056C8.81748 2.92224 8.02454 2.51068 7.15478 2.36252C6.95174 2.32959 6.76242 2.46404 6.72675 2.66433C6.69109 2.86462 6.82278 3.05943 7.02582 3.09235ZM12.9852 5.73457C12.741 4.30234 12.0661 2.99907 11.0289 1.96193C9.9918 0.9248 8.68853 0.24984 7.2563 0.00564702C7.056 -0.0300216 6.86668 0.107165 6.83102 0.307458C6.79809 0.510495 6.93253 0.699813 7.13557 0.735482C8.41415 0.952237 9.58024 1.5586 10.5076 2.48324C11.435 3.41063 12.0386 4.57672 12.2554 5.8553C12.2856 6.03639 12.442 6.1626 12.6203 6.1626C12.6423 6.1626 12.6615 6.15985 12.6834 6.15711C12.8837 6.12693 13.0209 5.93487 12.9852 5.73457Z"
                                fill="#A49191" />
                            </svg>
                            {t('label-title.mobile-no')}.
                          </p>
                          <h5 className="mb-0">{tripDetail.user.phoneCode}{tripDetail.user.phone}</h5>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Trip Summary */}
                  <div className="col-12 mt-3">
                    <h4 className="mb-3">{t('heading-title.trip-summary')}</h4>
                    <div className="card">
                      <div className="card-body">
                        <p className="doted mb-0">{returnTrip.current ? t('label-title.return-trip') : t('label-title.one-way')}</p>
                        <div className="d-flex address py-3 border-doted justify-content-between align-items-end">
                          <div className="mb-3 mb-sm-0">
                            <p className="from mb-2">{t('label-title.from')}</p>
                            <h5 className="mb-0" title={tripDetail.pickup.address}>{tripDetail.pickup.address}</h5>
                          </div>
                          <div className="arrow">
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="18" viewBox="0 0 24 18" fill="none">
                              <path d="M1.16625 7.91671H20.2177L14.4837 2.18263C14.3802 2.0827 14.2977 1.96316 14.2409 1.83099C14.1841 1.69881 14.1542 1.55666 14.153 1.41282C14.1517 1.26897 14.1791 1.12632 14.2336 0.99318C14.2881 0.860043 14.3685 0.739086 14.4702 0.637369C14.572 0.535652 14.6929 0.455211 14.826 0.40074C14.9592 0.346269 15.1018 0.318859 15.2457 0.320109C15.3895 0.321359 15.5317 0.351244 15.6639 0.408021C15.796 0.464797 15.9156 0.547328 16.0155 0.650797L23.5988 8.23413C23.8019 8.43729 23.916 8.71279 23.916 9.00005C23.916 9.28731 23.8019 9.56281 23.5988 9.76596L16.0155 17.3493C15.8112 17.5466 15.5375 17.6558 15.2535 17.6534C14.9694 17.6509 14.6977 17.537 14.4969 17.3361C14.296 17.1352 14.1821 16.8635 14.1796 16.5795C14.1771 16.2954 14.2863 16.0218 14.4837 15.8175L20.2177 10.0834H1.16625C0.878931 10.0834 0.60338 9.96924 0.400215 9.76608C0.197052 9.56291 0.0829163 9.28736 0.0829163 9.00005C0.0829163 8.71273 0.197052 8.43718 0.400215 8.23402C0.60338 8.03085 0.878931 7.91671 1.16625 7.91671Z" fill="black" />
                            </svg>
                          </div>
                          <div>
                            <p className="from mb-2">{t('label-title.to')}</p>
                            <h5 className="mb-0" title={tripDetail.drop.address}>{tripDetail.drop.address}</h5>
                          </div>
                        </div>

                        <div className="d-flex pt-3 w-100">
                          <div className="trip-info d-flex align-items-center justify-content-between w-100">
                            <div className="pe-1 mb-3 mb-md-0">
                              <p className="from mb-2">
                                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="10" viewBox="0 0 12 10"
                                  fill="none" style={{ marginTop: '-2px' }}>
                                  <path
                                    d="M10.2422 5.28487H9.06755C8.90768 5.28487 8.7529 5.30671 8.60559 5.34688C8.31502 4.77566 7.72177 4.38318 7.03816 4.38318H4.96184C4.27823 4.38318 3.68498 4.77566 3.39441 5.34688C3.24387 5.30574 3.08851 5.28488 2.93245 5.28487H1.75781C0.788555 5.28487 0 6.07342 0 7.04268V8.92044C0 9.502 0.473133 9.97513 1.05469 9.97513H10.9453C11.5269 9.97513 12 9.502 12 8.92044V7.04268C12 6.07342 11.2114 5.28487 10.2422 5.28487ZM3.20402 6.14104V9.27203H1.05469C0.860836 9.27203 0.703125 9.11432 0.703125 8.92047V7.0427C0.703125 6.46115 1.17626 5.98802 1.75781 5.98802H2.93245C3.02789 5.98802 3.1203 6.00098 3.20829 6.02486C3.20559 6.06353 3.20417 6.10228 3.20402 6.14104ZM8.09285 9.27203H3.90715V6.14101C3.90715 5.55946 4.38028 5.08633 4.96184 5.08633H7.03816C7.61972 5.08633 8.09285 5.55946 8.09285 6.14101V9.27203ZM11.2969 8.92047C11.2969 9.11432 11.1392 9.27203 10.9453 9.27203H8.79598V6.14101C8.79583 6.10225 8.79441 6.0635 8.79171 6.02484C8.88162 6.0004 8.97438 5.98801 9.06755 5.98799H10.2422C10.8237 5.98799 11.2969 6.46112 11.2969 7.04268V8.92047Z"
                                    fill="#A49191" />
                                  <path
                                    d="M2.34522 1.96494C1.48391 1.96494 0.783203 2.66565 0.783203 3.52696C0.78318 4.38826 1.48391 5.08897 2.34522 5.08897C3.2065 5.08897 3.90723 4.38826 3.90723 3.52696C3.90723 2.66565 3.20652 1.96494 2.34522 1.96494ZM2.3452 4.38585C1.87159 4.38585 1.4863 4.00056 1.4863 3.52696C1.4863 3.05335 1.87159 2.66807 2.3452 2.66807C2.8188 2.66807 3.20409 3.05335 3.20409 3.52696C3.20409 4.00056 2.8188 4.38585 2.3452 4.38585ZM6.00006 0.0249023C4.84937 0.0249023 3.91323 0.961043 3.91323 2.11173C3.91323 3.26242 4.84937 4.19856 6.00006 4.19856C7.15075 4.19856 8.08689 3.26242 8.08689 2.11173C8.08689 0.961066 7.15075 0.0249023 6.00006 0.0249023ZM6.00006 3.49543C5.23708 3.49543 4.61636 2.87471 4.61636 2.11173C4.61636 1.34877 5.23708 0.728027 6.00006 0.728027C6.76305 0.728027 7.38377 1.34875 7.38377 2.11173C7.38377 2.87471 6.76305 3.49543 6.00006 3.49543ZM9.65491 1.96494C8.79362 1.96494 8.09289 2.66565 8.09289 3.52696C8.09291 4.38826 8.79362 5.08897 9.65491 5.08897C10.5162 5.08897 11.2169 4.38826 11.2169 3.52696C11.2169 2.66565 10.5162 1.96494 9.65491 1.96494ZM9.65491 4.38585C9.18133 4.38585 8.79602 4.00056 8.79602 3.52696C8.79604 3.05335 9.18133 2.66807 9.65491 2.66807C10.1285 2.66807 10.5138 3.05335 10.5138 3.52696C10.5138 4.00056 10.1285 4.38585 9.65491 4.38585Z"
                                    fill="#A49191" />
                                </svg> {t('label-title.passengers')}
                              </p>
                              <h5 className="mb-0">{tripDetail.user.pax}</h5>
                            </div>
                            <div className="pe-1 mb-3 mb-md-0">
                              <p className="from mb-2">
                                <svg xmlns="http://www.w3.org/2000/svg" style={{ marginTop: '-2px' }} width="12" height="12"
                                  viewBox="0 0 12 12" fill="none">
                                  <path
                                    d="M10.7494 2.12245H8.8125C8.80758 1.71511 8.64255 1.32606 8.35309 1.03941C8.06364 0.75276 7.673 0.591532 7.26563 0.590576H4.73438C4.327 0.591532 3.93637 0.75276 3.64691 1.03941C3.35746 1.32606 3.19243 1.71511 3.1875 2.12245H1.25063C1.08624 2.12245 0.923457 2.15486 0.771602 2.21783C0.619746 2.2808 0.481795 2.37308 0.365639 2.48941C0.249484 2.60574 0.157402 2.74383 0.0946629 2.89578C0.0319235 3.04773 -0.000243005 3.21056 3.46001e-06 3.37495V4.45308C-0.000579595 4.70007 0.0725386 4.94162 0.210003 5.14683V10.1606C0.2105 10.4919 0.342481 10.8096 0.576965 11.0437C0.811449 11.2778 1.12927 11.4093 1.46063 11.4093H10.5394C10.8707 11.4093 11.1886 11.2778 11.423 11.0437C11.6575 10.8096 11.7895 10.4919 11.79 10.1606V5.14308C11.9275 4.93787 12.0006 4.69632 12 4.44933V3.37495C12.0002 3.21056 11.9681 3.04773 11.9053 2.89578C11.8426 2.74383 11.7505 2.60574 11.6344 2.48941C11.5182 2.37308 11.3803 2.2808 11.2284 2.21783C11.0766 2.15486 10.9138 2.12245 10.7494 2.12245ZM4.73438 1.34058H7.26563C7.47455 1.34005 7.67527 1.42179 7.8244 1.56811C7.97352 1.71443 8.05906 1.91356 8.0625 2.12245H3.9375C3.94095 1.91356 4.02649 1.71443 4.17561 1.56811C4.32474 1.42179 4.52546 1.34005 4.73438 1.34058ZM0.750003 3.37495C0.7505 3.2425 0.803463 3.11565 0.897294 3.02217C0.991126 2.92869 1.11818 2.8762 1.25063 2.8762H10.7494C10.8818 2.8762 11.0089 2.92869 11.1027 3.02217C11.1965 3.11565 11.2495 3.2425 11.25 3.37495V4.45308C11.2495 4.5857 11.1966 4.71275 11.1028 4.80653C11.009 4.9003 10.882 4.95321 10.7494 4.9537H9.65625V4.52808C9.65625 4.42862 9.61674 4.33324 9.54642 4.26291C9.47609 4.19258 9.38071 4.15308 9.28125 4.15308H7.96875C7.8693 4.15308 7.77391 4.19258 7.70359 4.26291C7.63326 4.33324 7.59375 4.42862 7.59375 4.52808V4.94995H4.40625V4.52808C4.40625 4.42862 4.36674 4.33324 4.29642 4.26291C4.22609 4.19258 4.13071 4.15308 4.03125 4.15308H2.71875C2.6193 4.15308 2.52391 4.19258 2.45359 4.26291C2.38326 4.33324 2.34375 4.42862 2.34375 4.52808V4.94995H1.25063C1.11801 4.94946 0.990957 4.89655 0.897179 4.80278C0.8034 4.709 0.750497 4.58195 0.750003 4.44933V3.37495ZM8.34375 4.90683H8.90625V6.31308C8.90625 6.38767 8.87662 6.45921 8.82388 6.51195C8.77113 6.56469 8.6996 6.59433 8.625 6.59433C8.55041 6.59433 8.47887 6.56469 8.42613 6.51195C8.37339 6.45921 8.34375 6.38767 8.34375 6.31308V4.90683ZM3.09375 4.90683H3.65625V6.31308C3.65625 6.38767 3.62662 6.45921 3.57388 6.51195C3.52113 6.56469 3.4496 6.59433 3.375 6.59433C3.30041 6.59433 3.22887 6.56469 3.17613 6.51195C3.12339 6.45921 3.09375 6.38767 3.09375 6.31308V4.90683ZM10.5394 10.6631H1.46063C1.32818 10.6631 1.20113 10.6106 1.10729 10.5171C1.01346 10.4236 0.9605 10.2968 0.960003 10.1643V5.66433C1.05512 5.6877 1.15268 5.69965 1.25063 5.69995H2.34375V6.30933C2.34375 6.58283 2.4524 6.84513 2.6458 7.03853C2.8392 7.23193 3.1015 7.34058 3.375 7.34058C3.64851 7.34058 3.91081 7.23193 4.10421 7.03853C4.2976 6.84513 4.40625 6.58283 4.40625 6.30933V5.69995H7.59375V6.30933C7.59375 6.44475 7.62043 6.57885 7.67225 6.70397C7.72408 6.82909 7.80004 6.94277 7.8958 7.03853C7.99156 7.13429 8.10524 7.21025 8.23036 7.26208C8.35548 7.3139 8.48958 7.34058 8.625 7.34058C8.76043 7.34058 8.89453 7.3139 9.01965 7.26208C9.14476 7.21025 9.25845 7.13429 9.35421 7.03853C9.44997 6.94277 9.52593 6.82909 9.57775 6.70397C9.62958 6.57885 9.65625 6.44475 9.65625 6.30933V5.69995H10.7494C10.8473 5.69965 10.9449 5.6877 11.04 5.66433V10.1643C11.0385 10.2961 10.9851 10.422 10.8914 10.5147C10.7977 10.6074 10.6712 10.6593 10.5394 10.6593V10.6631Z"
                                    fill="#A49191" />
                                </svg> {t('label-title.bags')}
                              </p>
                              <h5 className="mb-0">{tripDetail.user.bags}</h5>
                            </div>
                            {tripDetail.pickupTime && (
                              <div className="pe-1 mb-3 mb-sm-0">
                                <p className="from mb-2 d-block">
                                  <svg xmlns="http://www.w3.org/2000/svg" style={{ marginTop: '-2px' }} width="10" height="10"
                                    viewBox="0 0 188 188" fill="none">
                                    <g clipPath="url(#clip0_133_175)">
                                      <path
                                        d="M165.969 14.6875H157.156V0H142.469V14.6875H45.5312V0H30.8438V14.6875H22.0312C9.88322 14.6875 0 24.5707 0 36.7188V165.969C0 178.117 9.88322 188 22.0312 188H165.969C178.117 188 188 178.117 188 165.969V36.7188C188 24.5707 178.117 14.6875 165.969 14.6875ZM173.312 165.969C173.312 170.018 170.018 173.312 165.969 173.312H22.0312C17.9819 173.312 14.6875 170.018 14.6875 165.969V69.0312H173.312V165.969ZM173.312 54.3438H14.6875V36.7188C14.6875 32.6694 17.9819 29.375 22.0312 29.375H30.8438V44.0625H45.5312V29.375H142.469V44.0625H157.156V29.375H165.969C170.018 29.375 173.312 32.6694 173.312 36.7188V54.3438Z"
                                        fill="#A49191" />
                                      <path
                                        d="M165.969 14.6875H157.156V0H142.469V14.6875H45.5312V0H30.8438V14.6875H22.0312C9.88322 14.6875 0 24.5707 0 36.7188V165.969C0 178.117 9.88322 188 22.0312 188H165.969C178.117 188 188 178.117 188 165.969V36.7188C188 24.5707 178.117 14.6875 165.969 14.6875ZM173.312 165.969C173.312 170.018 170.018 173.312 165.969 173.312H22.0312C17.9819 173.312 14.6875 170.018 14.6875 165.969V69.0312H173.312V165.969ZM173.312 54.3438H14.6875V36.7188C14.6875 32.6694 17.9819 29.375 22.0312 29.375H30.8438V44.0625H45.5312V29.375H142.469V44.0625H157.156V29.375H165.969C170.018 29.375 173.312 32.6694 173.312 36.7188V54.3438Z"
                                        stroke="white" />
                                      <path
                                        d="M27.9062 84.4531H42.5938V99.1406H27.9062V84.4531ZM57.2812 84.4531H71.9688V99.1406H57.2812V84.4531ZM86.6562 84.4531H101.344V99.1406H86.6562V84.4531ZM116.031 84.4531H130.719V99.1406H116.031V84.4531ZM145.406 84.4531H160.094V99.1406H145.406V84.4531ZM27.9062 113.828H42.5938V128.516H27.9062V113.828ZM57.2812 113.828H71.9688V128.516H57.2812V113.828ZM86.6562 113.828H101.344V128.516H86.6562V113.828ZM116.031 113.828H130.719V128.516H116.031V113.828ZM27.9062 143.203H42.5938V157.891H27.9062V143.203ZM57.2812 143.203H71.9688V157.891H57.2812V143.203ZM86.6562 143.203H101.344V157.891H86.6562V143.203ZM116.031 143.203H130.719V157.891H116.031V143.203ZM145.406 113.828H160.094V128.516H145.406V113.828Z"
                                        fill="#A49191" />
                                      <path
                                        d="M27.9062 84.4531H42.5938V99.1406H27.9062V84.4531ZM57.2812 84.4531H71.9688V99.1406H57.2812V84.4531ZM86.6562 84.4531H101.344V99.1406H86.6562V84.4531ZM116.031 84.4531H130.719V99.1406H116.031V84.4531ZM145.406 84.4531H160.094V99.1406H145.406V84.4531ZM27.9062 113.828H42.5938V128.516H27.9062V113.828ZM57.2812 113.828H71.9688V128.516H57.2812V113.828ZM86.6562 113.828H101.344V128.516H86.6562V113.828ZM116.031 113.828H130.719V128.516H116.031V113.828ZM27.9062 143.203H42.5938V157.891H27.9062V143.203ZM57.2812 143.203H71.9688V157.891H57.2812V143.203ZM86.6562 143.203H101.344V157.891H86.6562V143.203ZM116.031 143.203H130.719V157.891H116.031V143.203ZM145.406 113.828H160.094V128.516H145.406V113.828Z"
                                        stroke="white" />
                                    </g>
                                    <defs>
                                      <clipPath id="clip0_133_175">
                                        <rect width="188" height="188" fill="white" />
                                      </clipPath>
                                    </defs>
                                  </svg>
                                  {t('label-title.pickup-date')}
                                </p>
                                <h5 className="mb-0">
                                  {moment(tripDetail.pickupTime).tz(tripDetail.timezone).format('DD/MM/YYYY HH:mm')}
                                </h5>
                              </div>
                            )}
                            {returnTrip.current && tripDetail.returnPickupTime && (
                              <div className="pe-1 mb-3 mb-sm-0">
                                <p className="from mb-2 d-block">
                                  <svg xmlns="http://www.w3.org/2000/svg" style={{ marginTop: '-2px' }} width="10" height="10"
                                    viewBox="0 0 188 188" fill="none">
                                    <g clipPath="url(#clip0_133_175)">
                                      <path
                                        d="M165.969 14.6875H157.156V0H142.469V14.6875H45.5312V0H30.8438V14.6875H22.0312C9.88322 14.6875 0 24.5707 0 36.7188V165.969C0 178.117 9.88322 188 22.0312 188H165.969C178.117 188 188 178.117 188 165.969V36.7188C188 24.5707 178.117 14.6875 165.969 14.6875ZM173.312 165.969C173.312 170.018 170.018 173.312 165.969 173.312H22.0312C17.9819 173.312 14.6875 170.018 14.6875 165.969V69.0312H173.312V165.969ZM173.312 54.3438H14.6875V36.7188C14.6875 32.6694 17.9819 29.375 22.0312 29.375H30.8438V44.0625H45.5312V29.375H142.469V44.0625H157.156V29.375H165.969C170.018 29.375 173.312 32.6694 173.312 36.7188V54.3438Z"
                                        fill="#A49191" />
                                      <path
                                        d="M165.969 14.6875H157.156V0H142.469V14.6875H45.5312V0H30.8438V14.6875H22.0312C9.88322 14.6875 0 24.5707 0 36.7188V165.969C0 178.117 9.88322 188 22.0312 188H165.969C178.117 188 188 178.117 188 165.969V36.7188C188 24.5707 178.117 14.6875 165.969 14.6875ZM173.312 165.969C173.312 170.018 170.018 173.312 165.969 173.312H22.0312C17.9819 173.312 14.6875 170.018 14.6875 165.969V69.0312H173.312V165.969ZM173.312 54.3438H14.6875V36.7188C14.6875 32.6694 17.9819 29.375 22.0312 29.375H30.8438V44.0625H45.5312V29.375H142.469V44.0625H157.156V29.375H165.969C170.018 29.375 173.312 32.6694 173.312 36.7188V54.3438Z"
                                        stroke="white" />
                                      <path
                                        d="M27.9062 84.4531H42.5938V99.1406H27.9062V84.4531ZM57.2812 84.4531H71.9688V99.1406H57.2812V84.4531ZM86.6562 84.4531H101.344V99.1406H86.6562V84.4531ZM116.031 84.4531H130.719V99.1406H116.031V84.4531ZM145.406 84.4531H160.094V99.1406H145.406V84.4531ZM27.9062 113.828H42.5938V128.516H27.9062V113.828ZM57.2812 113.828H71.9688V128.516H57.2812V113.828ZM86.6562 113.828H101.344V128.516H86.6562V113.828ZM116.031 113.828H130.719V128.516H116.031V113.828ZM27.9062 143.203H42.5938V157.891H27.9062V143.203ZM57.2812 143.203H71.9688V157.891H57.2812V143.203ZM86.6562 143.203H101.344V157.891H86.6562V143.203ZM116.031 143.203H130.719V157.891H116.031V143.203ZM145.406 113.828H160.094V128.516H145.406V113.828Z"
                                        fill="#A49191" />
                                      <path
                                        d="M27.9062 84.4531H42.5938V99.1406H27.9062V84.4531ZM57.2812 84.4531H71.9688V99.1406H57.2812V84.4531ZM86.6562 84.4531H101.344V99.1406H86.6562V84.4531ZM116.031 84.4531H130.719V99.1406H116.031V84.4531ZM145.406 84.4531H160.094V99.1406H145.406V84.4531ZM27.9062 113.828H42.5938V128.516H27.9062V113.828ZM57.2812 113.828H71.9688V128.516H57.2812V113.828ZM86.6562 113.828H101.344V128.516H86.6562V113.828ZM116.031 113.828H130.719V128.516H116.031V113.828ZM27.9062 143.203H42.5938V157.891H27.9062V143.203ZM57.2812 143.203H71.9688V157.891H57.2812V143.203ZM86.6562 143.203H101.344V157.891H86.6562V143.203ZM116.031 143.203H130.719V157.891H116.031V143.203ZM145.406 113.828H160.094V128.516H145.406V113.828Z"
                                        stroke="white" />
                                    </g>
                                    <defs>
                                      <clipPath id="clip0_133_175">
                                        <rect width="188" height="188" fill="white" />
                                      </clipPath>
                                    </defs>
                                  </svg>
                                  {t('label-title.return-date')}
                                </p>
                                <h5 className="mb-0">
                                  {moment(tripDetail.returnPickupTime).tz(tripDetail.timezone).format('DD/MM/YYYY HH:mm')}
                                </h5>
                              </div>
                            )}
                            <div className="pe-1 mb-3 mb-md-0">
                              <p className="from mb-2">
                                <svg width="26" height="13" viewBox="0 0 26 13" fill="none" style={{ marginTop: '-2px' }}
                                  xmlns="http://www.w3.org/2000/svg">
                                  <path
                                    d="M6.09375 10.2344C6.4303 10.2344 6.70312 9.96155 6.70312 9.625C6.70312 9.28845 6.4303 9.01562 6.09375 9.01562C5.7572 9.01562 5.48438 9.28845 5.48438 9.625C5.48438 9.96155 5.7572 10.2344 6.09375 10.2344Z"
                                    fill="#A49191" />
                                  <path
                                    d="M20.7188 10.2344C21.0553 10.2344 21.3281 9.96155 21.3281 9.625C21.3281 9.28845 21.0553 9.01562 20.7188 9.01562C20.3822 9.01562 20.1094 9.28845 20.1094 9.625C20.1094 9.96155 20.3822 10.2344 20.7188 10.2344Z"
                                    fill="#A49191" />
                                  <path
                                    d="M24.243 4.80262L19.7575 4.12215L15.4618 0.58696C15.0617 0.258507 14.5602 0.0787147 14.0425 0.0781318H8.09352C7.75827 0.0773042 7.42718 0.152309 7.12502 0.297532C6.82286 0.442755 6.55746 0.654436 6.34867 0.916733L3.76959 4.14063H2.03125C1.54657 4.14117 1.08189 4.33395 0.739164 4.67667C0.396441 5.01939 0.203663 5.48407 0.203125 5.96876V8.00001C0.203784 8.5924 0.439402 9.16034 0.858286 9.57922C1.27717 9.99811 1.84511 10.2337 2.4375 10.2344H3.52488C3.66192 10.8127 3.9901 11.3278 4.45629 11.6965C4.92248 12.0651 5.49942 12.2656 6.09375 12.2656C6.68808 12.2656 7.26502 12.0651 7.73121 11.6965C8.19741 11.3278 8.52558 10.8127 8.66262 10.2344H18.1499C18.2869 10.8127 18.6151 11.3278 19.0813 11.6965C19.5475 12.0651 20.1244 12.2656 20.7188 12.2656C21.3131 12.2656 21.89 12.0651 22.3562 11.6965C22.8224 11.3278 23.1506 10.8127 23.2876 10.2344H23.9688C24.4534 10.2338 24.9181 10.0411 25.2608 9.69834C25.6036 9.35562 25.7963 8.89094 25.7969 8.40626V6.61012C25.7991 6.17219 25.6431 5.74819 25.3576 5.4161C25.0721 5.08401 24.6763 4.86616 24.243 4.80262ZM14.6877 1.52824L17.8622 4.14063H11.9844V1.29688H14.0425C14.2779 1.2972 14.5058 1.37894 14.6877 1.52824ZM7.30041 1.67774C7.39535 1.55858 7.516 1.46242 7.65334 1.39647C7.79068 1.33052 7.94116 1.29647 8.09352 1.29688H8.32812V4.14063H5.33036L7.30041 1.67774ZM6.09375 11.0469C5.81253 11.0469 5.53762 10.9635 5.3038 10.8073C5.06997 10.651 4.88773 10.4289 4.78011 10.1691C4.67249 9.90932 4.64433 9.62343 4.6992 9.34761C4.75406 9.0718 4.88948 8.81844 5.08833 8.61959C5.28719 8.42074 5.54054 8.28532 5.81636 8.23045C6.09217 8.17559 6.37806 8.20375 6.63788 8.31137C6.89769 8.41898 7.11976 8.60123 7.276 8.83506C7.43223 9.06888 7.51562 9.34379 7.51562 9.62501C7.51519 10.002 7.36525 10.3634 7.09869 10.6299C6.83213 10.8965 6.47072 11.0465 6.09375 11.0469ZM20.7188 11.0469C20.4375 11.0469 20.1626 10.9635 19.9288 10.8073C19.695 10.651 19.5127 10.4289 19.4051 10.1691C19.2975 9.90932 19.2693 9.62343 19.3242 9.34761C19.3791 9.0718 19.5145 8.81844 19.7133 8.61959C19.9122 8.42074 20.1655 8.28532 20.4414 8.23045C20.7172 8.17559 21.0031 8.20375 21.2629 8.31137C21.5227 8.41898 21.7448 8.60123 21.901 8.83506C22.0572 9.06888 22.1406 9.34379 22.1406 9.62501C22.1402 10.002 21.9903 10.3634 21.7237 10.6299C21.4571 10.8965 21.0957 11.0465 20.7188 11.0469ZM24.5781 8.40626C24.5779 8.56782 24.5137 8.72271 24.3995 8.83696C24.2852 8.9512 24.1303 9.01546 23.9688 9.01563H23.2876C23.1506 8.43732 22.8224 7.92217 22.3562 7.55354C21.89 7.18491 21.3131 6.98437 20.7188 6.98437C20.1244 6.98437 19.5475 7.18491 19.0813 7.55354C18.6151 7.92217 18.2869 8.43732 18.1499 9.01563H8.66262C8.52558 8.43732 8.19741 7.92217 7.73121 7.55354C7.26502 7.18491 6.68808 6.98437 6.09375 6.98437C5.49942 6.98437 4.92248 7.18491 4.45629 7.55354C3.9901 7.92217 3.66192 8.43732 3.52488 9.01563H2.4375C2.16823 9.01532 1.91009 8.90822 1.71969 8.71782C1.52929 8.52742 1.42218 8.26927 1.42188 8.00001V5.96876C1.42205 5.80719 1.48631 5.6523 1.60055 5.53806C1.71479 5.42381 1.86969 5.35956 2.03125 5.35938H8.9375C9.09912 5.35938 9.25411 5.29518 9.36839 5.1809C9.48267 5.06662 9.54688 4.91162 9.54688 4.75001V1.29688H10.7656V4.75001C10.7656 4.91162 10.8298 5.06662 10.9441 5.1809C11.0584 5.29518 11.2134 5.35938 11.375 5.35938H19.7886L24.0602 6.00755C24.2039 6.03022 24.335 6.10327 24.4299 6.21365C24.5248 6.32404 24.5773 6.46456 24.5781 6.61012V8.40626Z"
                                    fill="#A49191" />
                                  <path
                                    d="M12.1875 5.76562H11.375C11.2134 5.76562 11.0584 5.82983 10.9441 5.94411C10.8298 6.05839 10.7656 6.21338 10.7656 6.375C10.7656 6.53662 10.8298 6.69161 10.9441 6.80589C11.0584 6.92017 11.2134 6.98438 11.375 6.98438H12.1875C12.3491 6.98438 12.5041 6.92017 12.6184 6.80589C12.7327 6.69161 12.7969 6.53662 12.7969 6.375C12.7969 6.21338 12.7327 6.05839 12.6184 5.94411C12.5041 5.82983 12.3491 5.76562 12.1875 5.76562Z"
                                    fill="#A49191" />
                                  <path
                                    d="M6.09375 10.2344C6.4303 10.2344 6.70312 9.96155 6.70312 9.625C6.70312 9.28845 6.4303 9.01562 6.09375 9.01562C5.7572 9.01562 5.48438 9.28845 5.48438 9.625C5.48438 9.96155 5.7572 10.2344 6.09375 10.2344Z"
                                    fill="#A49191" />
                                  <path
                                    d="M20.7188 10.2344C21.0553 10.2344 21.3281 9.96155 21.3281 9.625C21.3281 9.28845 21.0553 9.01562 20.7188 9.01562C20.3822 9.01562 20.1094 9.28845 20.1094 9.625C20.1094 9.96155 20.3822 10.2344 20.7188 10.2344Z"
                                    fill="#A49191" />
                                </svg>
                              </p>
                              <h5 className="mb-0">{tripDetail.vehicle.name}</h5>
                            </div>
                            {tripDetail.user.flight && (
                              <div className="pe-1 mb-3 mb-md-0">
                                <p className="from mb-2">
                                  <svg xmlns="http://www.w3.org/2000/svg" className="input-icon" width="18" height="14" viewBox="0 0 18 14" fill="none">
                                    <path d="M0.923015 6.81504C0.941298 6.86705 0.970108 6.91471 1.00778 6.95469C1.04517 6.99437 1.09045 7.02559 1.14079 7.04601L3.13951 7.90995L2.86388 8.043L2.86328 8.04329C2.67561 8.13545 2.59712 8.36343 2.67809 8.55538L2.67808 8.55539L2.67887 8.55718L3.30783 9.97032C3.30786 9.97038 3.30789 9.97044 3.30791 9.9705C3.34913 10.0641 3.42482 10.1385 3.51971 10.1755L3.52053 10.1758C3.56204 10.1916 3.60614 10.1998 3.65067 10.1998M0.923015 6.81504C0.904658 6.76307 0.897168 6.70773 0.900956 6.65268C0.904747 6.59759 0.91976 6.54381 0.945165 6.49492L0.945565 6.49415L0.945568 6.49416L1.68644 5.0983L1.68674 5.09774C1.72682 5.02338 1.78954 4.96388 1.86612 4.92921C1.94278 4.8945 2.02852 4.88689 2.10998 4.90781L2.1108 4.90802L2.1108 4.90803L5.04695 5.68865L6.63701 4.90111L3.67653 2.77114L3.6762 2.7709C3.62747 2.73553 3.58773 2.68901 3.55984 2.63536C3.53196 2.58172 3.51659 2.52225 3.51474 2.46168L3.5147 2.46044L3.51471 2.46044C3.51119 2.2658 3.54984 2.07272 3.62792 1.89565C3.70599 1.71857 3.82156 1.5619 3.96626 1.43761L3.97102 1.43353L3.97113 1.43369C4.33364 1.15977 4.83477 1.16634 5.42704 1.39982L5.42826 1.4003L5.42825 1.40031L9.98348 3.26507L14.2178 1.18698C14.709 0.935463 15.2621 0.846191 15.8034 0.931375C16.0508 0.962647 16.2868 1.05708 16.4906 1.20597C16.6947 1.35516 16.8602 1.55437 16.9726 1.78565L16.974 1.7885L16.974 1.78852C17.3827 2.70129 16.7671 3.82971 15.6515 4.37456L15.6515 4.37456L11.9848 6.16466L10.1272 11.9216L10.1269 11.9224C9.91876 12.5493 9.6031 12.9488 9.16914 13.0673L9.16858 13.0675C9.08807 13.0889 9.00528 13.0999 8.92212 13.1L8.91856 13.1L8.91856 13.0999C8.62492 13.0899 8.34341 12.9781 8.11813 12.7829L8.11526 12.7805L8.11531 12.7804C8.07114 12.7391 8.03721 12.6879 8.01591 12.631C7.99461 12.5741 7.98643 12.513 7.9919 12.4525L7.99191 12.4524L8.4063 7.91768L3.81239 10.1634L3.81151 10.1639C3.76121 10.1878 3.70631 10.2002 3.65067 10.1998M0.923015 6.81504C0.923004 6.815 0.922992 6.81497 0.922981 6.81494L1.01733 6.7818L0.92305 6.81513C0.923039 6.8151 0.923027 6.81507 0.923015 6.81504ZM3.65067 10.1998C3.65055 10.1998 3.65042 10.1998 3.65029 10.1998L3.65105 10.0998V10.1998C3.65093 10.1998 3.6508 10.1998 3.65067 10.1998Z" fill="#9D9D9D" stroke="#9D9D9D" strokeWidth="0.2" />
                                  </svg>
                                </p>
                                <h5 className="mb-0">{tripDetail.user.flight}</h5>
                              </div>
                            )}
                          </div>
                        </div>

                        {tripDetail.note && (
                          <div className="d-flex pt-3 w-100">
                            {t('label-title.special-request')}: {tripDetail.note}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal show={showTracking} onHide={() => setShowTracking(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>{t('label-title.track', 'Track And Trace')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div ref={mapContainerRef} style={{ width: '100%', height: '500px', borderRadius: '8px' }} />
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default Summary;

